/**
 * The one way this app talks to the server.
 *
 * Every call goes through `request`. That is the point: 401 and 403 are
 * answered once, here, instead of being half-remembered on fifteen pages --
 * and a page that forgets is a page that renders an empty table to somebody
 * whose session quietly expired.
 *
 *   401  -> the session is gone. Back to /login.
 *   403 naming the password -> to /password. `auth.active_user` enforces that
 *        server-side; this is only the client keeping up with it.
 *
 * Money in every one of these types is integer cents. Nothing here divides.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;

  constructor(status: number, detail: string) {
    super(detail);
    this.status = status;
    this.detail = detail;
  }
}

/** Set once by the app shell, so this module never has to import the router. */
type Redirect = (to: string) => void;
let redirect: Redirect = () => {};
export function onUnauthenticated(handler: Redirect): void {
  redirect = handler;
}

export interface Received<T> {
  data: T;
  /** When this response landed. The anchor every age on screen counts from. */
  receivedAt: number;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<Received<T>> {
  let response: Response;
  try {
    response = await fetch(path, {
      // The session cookie is the whole authentication story. Without this the
      // browser omits it and every call is anonymous.
      credentials: "same-origin",
      ...init,
      headers:
        // JSON by default — but NOT for a FormData body: the browser sets
        // multipart/form-data with its own boundary there, and forcing a type
        // would break the upload (#17 payroll register).
        init.body !== undefined && !(init.body instanceof FormData)
          ? { "Content-Type": "application/json", ...(init.headers ?? {}) }
          : (init.headers ?? {}),
    });
  } catch {
    // A network failure is a real state with its own message. It is not an
    // empty result, and a table must never render it as one.
    throw new ApiError(0, "Could not reach the server.");
  }

  const receivedAt = Date.now();

  if (response.status === 204) {
    return { data: undefined as T, receivedAt };
  }

  let payload: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const detail =
      (payload as { detail?: string } | null)?.detail ?? response.statusText;

    const isAuthCall = path.startsWith("/api/auth/");
    if (response.status === 401 && !isAuthCall) {
      redirect("/login");
    }
    if (
      response.status === 403 &&
      detail.toLowerCase().includes("password") &&
      !isAuthCall
    ) {
      redirect("/password");
    }
    throw new ApiError(response.status, detail);
  }

  return { data: payload as T, receivedAt };
}

const get = <T>(path: string) => request<T>(path);
const post = <T>(path: string, body?: unknown) =>
  request<T>(path, {
    method: "POST",
    body: body === undefined ? undefined : JSON.stringify(body),
  });
const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: "PATCH", body: JSON.stringify(body) });
const put = <T>(path: string, body: unknown) =>
  request<T>(path, { method: "PUT", body: JSON.stringify(body) });
const del = <T>(path: string) => request<T>(path, { method: "DELETE" });

// ---------------------------------------------------------------------------
// What the server returns. Kept in step with api/routes/ by hand -- there is
// no code generation here, and these shapes are asserted by the API tests.
// ---------------------------------------------------------------------------
export type Role = "operator" | "approver" | "principal" | "builder";

/**
 * The roles the People screen may assign. `builder` is excluded on purpose: it
 * is Silverlock's own role and gates the Plaid sync and the approver bootstrap,
 * so minting one -- or editing one -- is a privilege-escalation path and stays
 * on the command line (`api/people_admin.py`).
 */
export type ManageableRole = "operator" | "approver" | "principal";
export const MANAGEABLE_ROLES: { key: ManageableRole; label: string }[] = [
  { key: "operator", label: "Operator — runs the book" },
  { key: "approver", label: "Approver — approves" },
  { key: "principal", label: "Principal — owner" },
];

/**
 * A value somebody turns, as opposed to the wiring a deploy sets.
 * `049_app_setting.sql` argues why the cutoff is a row rather than an env var.
 */
export interface AppSetting {
  key: string;
  /**
   * ⚠️ A STRING, all the way down. The value is a `numeric(5,4)` compared
   * against a stored confidence and copied onto every decision judged against
   * it; a JSON number is an IEEE double and 0.35 does not survive that round
   * trip exactly. The same rule money follows everywhere else here.
   */
  value: string;
  updated_at: string;
  updated_by: string | null;
  updated_by_name: string | null;
  /** What it shipped as. Shown BESIDE a changed value, never instead of it. */
  default: string | null;
  /**
   * ⚠️ Nobody has turned it since it shipped — which is NOT the same fact as
   * somebody deliberately setting it back to the default, and the note says
   * which it is looking at.
   */
  never_changed: boolean;
}

export interface ClassificationSettings {
  cutoff: AppSetting;
  /** Builder only, and it is a reach argument — see `routes/settings.py`. */
  may_change: boolean;
}

/** One address on an alert (063). `enabled` false means kept but muted — a
 *  different act from removing them, so it reads back as its own state. */
export interface AlertRecipient {
  /** The subscription row id. */
  id: string;
  /** The subscribed login (064); alerts go to users, not typed-in addresses. */
  user_id: string;
  full_name: string;
  email: string;
  enabled: boolean;
  /** A subscription for a deactivated user is kept but never mailed. */
  is_active: boolean;
  created_at: string;
}

/**
 * The subscribers to one alert kind, plus the honest arming state.
 *
 * `armed` (the switch) and `sendable` (a key to send through) are returned
 * because a list of subscribers with the alert OFF emails no one — the
 * silent-failure state the Alerts screen exists to surface. The list is
 * read-only here: subscriptions are toggled per person on the People screen
 * (`manage_at`), because what someone is told about and what they can see are
 * one question. Each subscriber's mail is already scoped to their own reach.
 */
export interface AlertRecipients {
  event_type: string;
  recipients: AlertRecipient[];
  manage_at: string;
  armed: boolean;
  sendable: boolean;
}

/**
 * One recipient of a group's daily balance snapshot (096). Unlike an alert
 * recipient, this is a login OR a free-typed email (`kind`), and the snapshot is
 * the one mail that deliberately reaches a principal with no account.
 */
export interface SnapshotRecipient {
  id: string;
  kind: "user" | "email";
  /** Set only for a `user` row; its name/address are read live from the login. */
  user_id: string | null;
  full_name: string | null;
  email: string;
  /** The name to show beside a free email; null for a user (its name is used). */
  label: string | null;
  enabled: boolean;
  /** null for a free email (no account to deactivate); only a user row dims. */
  is_active: boolean | null;
}

/** One group's snapshot recipient list, with whether a scheduled job feeds it. */
export interface SnapshotGroup {
  group_id: string;
  group_name: string;
  /** A configured SNAPSHOT_JOBS entry targets this group, so the list sends. */
  has_job: boolean;
  recipients: SnapshotRecipient[];
}

/**
 * The Snapshot-emails screen: the groups that have a scheduled snapshot or a
 * saved recipient, every live group for the add picker, and the honest arming
 * state (a full list with the job OFF or no sender mails no one).
 */
export interface SnapshotRecipients {
  groups: SnapshotGroup[];
  available_groups: { id: string; name: string }[];
  /** Whether the viewer may change the lists (an access manager). */
  may_edit: boolean;
  armed: boolean;
  sendable: boolean;
}

export interface User {
  id: string;
  email: string;
  full_name: string;
  role: Role;
  must_change_password: boolean;
  /** May grant and revoke reach on the People screen (037). Three people. */
  manages_access: boolean;
}

export interface Entity {
  id: string;
  name: string;
  kind: "company" | "personal" | "trust" | "charity";
  in_scope: boolean | null;
  retired_on: string | null;
  may_approve: boolean;
  account_count: number;
  /**
   * At least one account under this entity is clinical-adjacent.
   *
   * ⚠️ Treated in the UI as "probably one of the ABA entities" and labelled
   * as unconfirmed, because that is exactly what it is: a name-pattern match
   * made at seed time that nobody has ever signed off. It is not a claim
   * about what the company does.
   */
  phi_adjacent: boolean;
  /** See `EntityCategory`. NULL means nobody has classified this entity. */
  category: EntityCategory | null;
  /**
   * Whether a PERSON ruled on `category`, as opposed to the migration
   * proposing it. Never collapsed into `category` itself: a proposal and a
   * decision are different facts, and the screen has to be able to say which
   * one it is looking at. False on every ABA/investment row until S24 closes.
   */
  category_confirmed: boolean;
}

/**
 * What an entity is FOR — one level below `kind`, and the axis the entity
 * filters actually group on.
 *
 * `kind` could not do this job: fifty of fifty-six rows are `company`, so a
 * filter on it offers "all of them" and "a handful". And it is wrong in the
 * other direction too — `13 Woodland` and `Vineyard Family Holdings` are
 * companies by kind and personal by whose money is in them, which is the
 * distinction `personal_entity` exists to carry.
 *
 * `null` is a real state: nobody has classified it. Rendered as its own group
 * rather than swept into an "other", because a visible unclassified row gets
 * fixed and one filed under a plausible heading does not.
 */
export type EntityCategory =
  | "personal"
  | "personal_entity"
  | "operating"
  | "investment"
  | "charity";

/** Heading, and the one-line gloss under it. Ordered as the filter offers them. */
export const ENTITY_CATEGORIES: {
  key: EntityCategory;
  label: string;
  note: string;
}[] = [
  {
    key: "personal",
    label: "Personal",
    note: "an individual's own money, in their own name",
  },
  {
    key: "personal_entity",
    label: "Personal — under an entity name",
    note: "personal money held through a company: 13 Woodland, 2 Murray, 4 S Rigaud, the Vineyard holdings",
  },
  {
    key: "operating",
    label: "Operating company",
    note: "the ABA / clinical companies",
  },
  {
    key: "investment",
    label: "Investment",
    note: "funds, holding vehicles and asset management",
  },
  { key: "charity", label: "Charity", note: "" },
];

export const ENTITY_CATEGORY_LABEL: Record<EntityCategory, string> =
  Object.fromEntries(
    ENTITY_CATEGORIES.map((c) => [c.key, c.label]),
  ) as Record<EntityCategory, string>;

export interface EntityDetail extends Entity {
  aliases: string[];
  company_file: string | null;
  /**
   * Whether this company keeps books in QuickBooks at all (S27, migration
   * 029). `null` means nobody has said, which after 029 should not occur --
   * it is kept in the type because a NEW entity added later starts there.
   * `monitor_only` is not out of scope: the bank feed is unaffected.
   */
  books_status: "in_quickbooks" | "monitor_only" | null;
  books_note: string | null;
  /**
   * NULL — not zero — when nothing under this entity has ever been read. An
   * entity we have no reading for is not an entity holding nothing.
   */
  cash_cents: number | null;
  investment_cents: number | null;
  debt_cents: number | null;
  accounts_with_reading: number;
  oldest_reading_age: number | null;
}

export interface Account {
  id: string;
  name: string;
  account_type: string;
  /**
   * cash | investment | debt — the single definition of what this account's
   * balance MEANS, computed by `account_class()` in the database rather than
   * re-derived here. It decides which feed the activity comes from, and it is
   * why brokerage money is never added to a cash total.
   */
  account_class: "cash" | "investment" | "debt" | null;
  /**
   * The currency a person reads this account's balance in (074). "USD" for all
   * but the Israeli account. `balance_cents` is ALWAYS dollars regardless — a
   * non-USD account's native figure is converted at entry — so this only tells
   * the entry screen to ask for a shekel→dollar rate and lets a screen label
   * the account as what it is.
   */
  currency: "USD" | "ILS";
  last4: string | null;
  /** NULL means no floor decided. It is not zero. */
  cash_floor_cents: number | null;
  phi_adjacent: boolean;
  retired_on: string | null;
  entity_id: string;
  entity_name: string;
  /** The entity's `category`, carried down so `/accounts` can group by it. */
  entity_category: EntityCategory | null;
  entity_category_confirmed: boolean;
  institution_id: string;
  institution_name: string;
  read_method: string;
  login_label: string | null;
  connection_status: string | null;
  /**
   * When the TRANSACTION feed on this account's login was last pulled. Null
   * means never — which is why `last_movement_on` being null cannot on its
   * own be read as "nothing happened".
   */
  transactions_synced_at: string | null;
  /** The newest posting date on this account. Null means no rows, and see above. */
  last_movement_on: string | null;
  balance_cents: number | null;
  available_cents: number | null;
  /**
   * What the floor is measured against: `available_cents` when the bank
   * reports one, `balance_cents` when it does not. Decided in the
   * `account_current_balance` view (schema/042), not here, so the alert and
   * the screen cannot disagree.
   */
  spendable_cents: number | null;
  as_of: string | null;
  /** Seconds. Null when the account has never been read. */
  age: number | null;
  /**
   * True only when the institution reported its own timestamp for this
   * reading. False means our read time stood in and `age` is a LOWER BOUND on
   * staleness, not a measurement of it. `schema/019_balance_as_of.sql`.
   */
  as_of_reported: boolean | null;
  staleness_warn_minutes: number;
}

export interface AccountDetail extends Account {
  cash_floor_source: string | null;
  last_synced_at: string | null;
  approver_name: string | null;
  approver_user_id: string | null;
  /**
   * ⚠️ Whether the named approver could actually approve anything here. On the
   * real book this is FALSE for every account that names anybody — 81 of them
   * — because no approve grant has ever been issued. `approver_name` says who
   * the bank sheet appointed; this says whether the system would honour it.
   */
  approver_may_approve: boolean;
  entity_kind: string;
  connection_id: string | null;
  /**
   * The spendable cash a person asserts is inside this account beyond what a
   * feed can see — the settled slice of a brokerage (071). NULL means none
   * asserted, which is not zero. Carved into the cash line and out of the
   * investment line by the server; the `as_of`/`set_by` say when it was last
   * confirmed and by whom, which is what lets a reader tell current from stale.
   */
  manual_invested_cents: number | null;
  manual_invested_as_of: string | null;
  manual_invested_set_by: string | null;
  manual_invested_set_by_name: string | null;
}

export interface Observation {
  id: string;
  balance_cents: number;
  available_cents: number | null;
  as_of: string;
  observed_at: string;
  read_method: string;
  age: number;
  /**
   * True only when the institution reported its own timestamp for this
   * reading. False means our read time stood in and `age` is a LOWER BOUND on
   * staleness, not a measurement of it. `schema/019_balance_as_of.sql`.
   */
  as_of_reported: boolean | null;
}

export interface Connection {
  id: string;
  login_label: string;
  status: string;
  last_error: string | null;
  last_synced_at: string | null;
  institution_name: string;
  read_method: string;
  accounts_linked: number;
  /** A Plaid login whose transactions have never been pulled — a reconnect's
   *  automatic first pull has not landed, so the manual pull is owed. */
  transactions_never_synced?: boolean;
  /** Accounts the bank returned on this login that are still Held, not matched
   *  to a registry row. A new login whose accounts are all Held reads as
   *  accounts_linked = 0 but is NOT an empty husk — it can be refreshed and its
   *  accounts migrated. */
  held_count?: number;
}

/** The dry-run of replacing a broken login with a new one — what would move,
 *  what would stay, what is new, what is refused. Writes nothing. */
export interface MigrationPlan {
  source: { id: string; login_label: string };
  target: { id: string; login_label: string };
  moves: {
    account_id: string;
    name: string;
    entity_name: string;
    last4: string | null;
    held_id: string;
  }[];
  stays_on_source: { account_id: string; name: string; last4: string | null; reason: string }[];
  new_on_target: { held_id: string; name: string; last4: string | null; reason: string }[];
  ambiguous: { account_id: string; name: string; last4: string | null; reason: string }[];
  will_move: number;
  source_empties: boolean;
}

/**
 * Refreshing the two transaction feeds from the site — `GET /api/sync` and
 * `POST /api/sync/run`. Builder-only, both of them.
 *
 * ⚠️ **`transactions` and `investments` are two Plaid products, not two views
 * of one.** Only one login in this book carries both, and every type below is
 * per-feed for that reason. There is no shape here that means "both", and one
 * should not be added: a combined call would attempt a brokerage pull on every
 * bank login and get back a consent error that means nothing.
 */
export type SyncFeed = "transactions" | "investments";

export interface SyncLoginPlan {
  connection_id: string;
  login_label: string;
  institution_name: string;
  status: string;
  /** Every account on the login for transactions; only the brokerage ones for investments. */
  accounts: number;
  rows_held: number;
  /** When this feed was last pulled for this login. Null is NEVER, not old. */
  synced_at: string | null;
  synced_ago: number | null;
  never_synced: boolean;
  would_call: boolean;
  /** Why it would not be called. Shown verbatim; never summarised to "skipped". */
  skip_reason: string | null;
  /**
   * A range, not a number, and the range is the honest answer. One Plaid page
   * is one billed call: an incremental pull is nearly always one, and a first
   * sync is however many the history takes. Rendering only the ceiling makes
   * every sync look ruinous; rendering only the floor makes a first sync look
   * free.
   */
  calls_at_least: number;
  calls_at_most: number;
  /** True when a sync would ask the bank for the login's whole history. */
  full_history: boolean;
}

export interface SyncFeedPlan {
  feed: SyncFeed;
  logins: SyncLoginPlan[];
  logins_callable: number;
  calls_at_least: number;
  calls_at_most: number;
  never_synced: string[];
}

export interface SyncRun {
  id: number;
  feed: SyncFeed;
  login_label: string;
  started_at: string;
  /** Null means still in flight. Nothing else may start while one is. */
  finished_at: string | null;
  pages: number;
  added: number;
  changed: number;
  unchanged: number;
  removed: number;
  /** Rows deliberately not stored: unmatched accounts, or ones nobody has ruled on. */
  skipped: number;
  full_history: boolean;
  note: string | null;
  error: string | null;
  requested_by: string;
}

export interface SyncState {
  feeds: Record<SyncFeed, SyncFeedPlan>;
  in_flight: {
    id: number;
    feed: SyncFeed;
    login_label: string;
    started_at: string;
  } | null;
  recent: SyncRun[];
  cooldown_seconds: number;
}

export interface SyncCounts {
  added: number;
  changed: number;
  unchanged: number;
  removed: number;
  skipped: number;
  pages: number;
  note: string;
}

export interface SyncOutcome {
  feed: SyncFeed;
  login_label: string;
  institution_name: string;
  /** Whether a bank was actually called. False for the cooldown and the dry run. */
  spent: boolean;
  /**
   * The bank was called and refused. NOT an HTTP error: the feed modules
   * record a bad login on the connection row rather than raising, so a failed
   * pull arrives as 200 with counts of zero — which is exactly what a quiet
   * week looks like. This is the only field that tells them apart.
   */
  failed: boolean;
  run_id: number | null;
  full_history: boolean;
  counts: SyncCounts | null;
  classified: {
    considered?: number;
    names_matched?: number;
    paired?: number;
    ambiguous?: number;
    error?: string;
  } | null;
  note: string;
  synced_at: string | null;
  /** Present only on a request that did not carry `for_real`. */
  dry_run?: boolean;
  would?: SyncLoginPlan;
}

export interface CoverageLogin {
  institution: string;
  login_label: string;
  status: string;
  accounts_linked: number;
  accounts_still_unlinked: number;
  unexpected_pending: number;
  last_synced_at: string | null;
}

/**
 * What `GET /api/dashboard` returns — reach-scoped account counts plus the two
 * linking-desk integers. **No `by_login`, deliberately** (`P19`, 2026-08-27):
 * that array carries institution names and login labels, and the dashboard is
 * served on every page to every viewer, including one granted nothing.
 *
 * Kept as a separate type from `Coverage` rather than marking `by_login`
 * optional. Optional would let `board.coverage.by_login?.map(...)` compile and
 * then silently render nothing, which is how the leak comes back the other way
 * round — someone "restores" it because the UI looks broken.
 */
export interface DashboardCoverage {
  accounts_total: number;
  accounts_linked: number;
  accounts_unlinked: number;
  held_for_review: number;
  plaid_items_used: number;
  plaid_item_limit: number;
}

/** Whole-book, from `GET /api/connections/coverage` — the linking screen only. */
export interface Coverage extends DashboardCoverage {
  by_login: CoverageLogin[];
}

export interface HeldAccount {
  id: string;
  plaid_name: string;
  plaid_official_name: string | null;
  plaid_mask: string | null;
  plaid_type: string | null;
  plaid_subtype: string | null;
  note: string | null;
  first_seen_at: string;
  institution_name: string;
  login_label: string;
  /** The bank login this account was returned on, so a per-login view can show
   *  just its own held accounts. */
  bank_connection_id: string;
}

export interface Institution {
  id: string;
  name: string;
  read_method: string;
  plaid_id: string | null;
  staleness_warn_minutes: number;
  account_count: number;
  accounts_linked: number;
  connection_count: number;
  oldest_reading_age: number | null;
  /**
   * Whether EVERY reading behind `oldest_reading_age` carries the bank's own
   * timestamp. One that does not makes the group's age a lower bound, so this
   * is `bool_and` and not the oldest row's own flag.
   */
  all_readings_reported: boolean;
}

export interface ActivityEvent {
  seq: number;
  id: string;
  event_type: string;
  aggregate_type: string;
  aggregate_id: string;
  occurred_at: string;
  recorded_at: string;
  actor_type: string;
  actor_name: string | null;
  actor_detail: string | null;
  source: string;
  payload: Record<string, unknown>;
  subject: string | null;
}

export interface Person {
  id: string;
  email: string;
  full_name: string;
  role: Role;
  is_active: boolean;
  has_password: boolean;
  must_change_password: boolean;
  last_login_at: string | null;
  entities_visible: number;
  entities_approvable: number;
  active_sessions: number;
  accounts_approved: number;
  /**
   * NAMES, over the intersection of their reach and the viewer's own. The
   * counts above are of everything; a name outside the viewer's reach is
   * never here, so `entities.length < entities_visible` means "and some you
   * cannot see". Sarah asked to see who sees what (2026-09-15).
   */
  entities: { id: string; name: string; may_approve: boolean }[];
  approver_of: { id: string; name: string; last4: string | null; entity: string }[];
  manages_access: boolean;
  /**
   * Subscribed to the cash-floor alert (064). Toggled on this screen; the mail
   * they would get is already scoped to their reach, so this is just "told or
   * not". A deactivated person is never mailed whatever this says.
   */
  gets_floor_alert: boolean;
}

/** The viewer's reach, stated so no total is ever shown without it. */
export interface Reach {
  entities_visible: number;
  accounts_visible: number;
  accounts_with_reading: number;
  oldest_reading_age: number | null;
  /**
   * The newest reading in this viewer's book, in seconds. A different
   * question from `oldest_reading_age` and the one somebody asks straight
   * after pressing Refresh: *did that do anything?* Null when nothing has
   * ever been read, which is not an age of zero.
   */
  newest_reading_age: number | null;
}

/**
 * What every exception carries, whatever kind it is.
 *
 * `case_status` is the state of the CASE. It is deliberately not called
 * `status`, because a connection problem already has a `status` meaning
 * whether the bank login is `reauth_required` or `revoked` — and two things
 * called status on one row is how a screen ends up saying "this login is
 * dismissed".
 *
 * `case_id` is null when nobody and nothing has touched this yet. That is not
 * an error state: the condition is true, the sweep has not reached it, and it
 * counts as open. Act on it with `case_type` + `subject_id`, never `case_id`.
 *
 * `age_seconds` is how long the CASE has been open. On a breach, `age` is a
 * different number — how old the balance reading is. Both are useful and they
 * answer different questions.
 */
export interface CaseState {
  case_id: string | null;
  case_type:
    | "floor_breach"
    | "connection_problem"
    | "missing_payment"
    | "unreconciled_balance";
  subject_id: string;
  case_status: "open" | "snoozed" | "dismissed";
  /** What was stored before the effective status was worked out. */
  stored_status: "open" | "snoozed" | "dismissed" | null;
  decided_magnitude: number | null;
  decided_by_name: string | null;
  decided_at: string | null;
  note: string | null;
  remind_at: string | null;
  reminder_due: boolean | null;
  raised_at: string | null;
  last_seen_at: string | null;
  age_seconds: number;
}

export interface DashboardBreach extends CaseState {
  account_id: string;
  account_name: string;
  entity_name: string;
  institution_name: string;
  last4: string | null;
  balance_cents: number;
  available_cents: number | null;
  /** The figure the breach was decided on. See `Account.spendable_cents`. */
  spendable_cents: number;
  cash_floor_cents: number;
  shortfall_cents: number;
  magnitude: number;
  as_of: string;
  age: number;
  /**
   * True only when the institution reported its own timestamp for this
   * reading. False means our read time stood in and `age` is a LOWER BOUND on
   * staleness, not a measurement of it. `schema/019_balance_as_of.sql`.
   */
  as_of_reported: boolean | null;
  staleness_warn_minutes: number;
}

export interface DashboardStale {
  account_id: string;
  account_name: string;
  entity_name: string;
  institution_name: string;
  as_of: string | null;
  age: number | null;
  /**
   * True only when the institution reported its own timestamp for this
   * reading. False means our read time stood in and `age` is a LOWER BOUND on
   * staleness, not a measurement of it. `schema/019_balance_as_of.sql`.
   */
  as_of_reported: boolean | null;
  staleness_warn_minutes: number;
}

/**
 * `accounts_affected` is YOUR count, not the book's.
 *
 * The stored magnitude on a connection case is whole-book — it is what the
 * sweep compares against — and it never reaches the browser, because telling
 * somebody who can see three accounts that forty are affected is not a number
 * about their book.
 */
export interface DashboardConnectionProblem extends CaseState {
  connection_id: string;
  institution_name: string;
  login_label: string;
  status: string;
  last_error: string | null;
  accounts_affected: number;
}

/**
 * A payment Orbit recorded as sent that the bank feed has not shown (061).
 *
 * `account_name` is the VENDOR here (the band renders it as the headline, the
 * same slot a breach fills with the account name). `magnitude` is the payment's
 * amount — the money that may not have left. Shadow-first: these rows only
 * arrive once `MISSING_PAYMENT_ENABLED` is on.
 */
export interface DashboardMissingPayment extends CaseState {
  /** The vendor name — the band's headline. */
  account_name: string;
  entity_name: string;
  magnitude: number;
  method: string;
  bank_reference: string | null;
  released_on: string | null;
  scheduled_for: string | null;
}

/**
 * An account whose balance moved with no transaction to explain it (101).
 *
 * `magnitude` is the size of the unexplained movement; `last_txn_on` is the day
 * the transaction feed stopped, so the band can read "X moved, nothing recorded
 * since <date>". The headline (`account_name`) is the account, like a breach.
 * Shadow-first: these rows only arrive once `UNRECONCILED_BALANCE_ENABLED` is on.
 */
export interface DashboardUnreconciled extends CaseState {
  account_id: string;
  account_name: string;
  entity_name: string;
  institution_name: string;
  last4: string | null;
  balance_cents: number;
  magnitude: number;
  as_of: string;
  age: number;
  as_of_reported: boolean | null;
  /** The newest transaction's posting date — when the feed went quiet. */
  last_txn_on: string | null;
}

export type ExceptionCase =
  | DashboardBreach
  | DashboardConnectionProblem
  | DashboardMissingPayment
  | DashboardUnreconciled;

export interface CaseCounts {
  open: number;
  snoozed: number;
  dismissed: number;
  /** snoozed + dismissed. Still true; decided about; out of the count. */
  acknowledged: number;
  total_live: number;
}

/** A case that closed because the problem went away. The shrinking pile. */
export interface ClosedCase {
  id: string;
  case_type: string;
  subject_id: string;
  account_name: string;
  last4: string | null;
  entity_name: string;
  magnitude: number;
  raised_at: string;
  closed_at: string;
  resolution: string;
  lasted_seconds: number;
}

/**
 * A row in the position, split three ways rather than added into one number.
 *
 * Cash is money that can pay something this week. `investment_cents` is held
 * but not spendable without selling first. `debt_cents` is money OWED and is
 * stored POSITIVE — a bigger number is worse. Never sum these into a headline:
 * the question this screen answers is "can we cover what is going out", and
 * only cash answers it.
 */
export interface PositionRow {
  id: string;
  name: string;
  cash_cents: number;
  investment_cents: number;
  debt_cents: number;
  accounts_with_reading: number;
  accounts_total: number;
  oldest_reading_age: number | null;
  /** See `Institution.all_readings_reported`. */
  all_readings_reported: boolean;
  /** Present on `/api/position/scoped` only: what moved today, per entity. */
  in_today_cents?: number;
  out_today_cents?: number;
  movements_today?: number;
}

/** One entity-and-bank cell of the position (059 reshape): an entity broken
 * down by the bank its money sits in, so the Position page can nest the banks
 * under each entity instead of lumping them into one figure. */
export interface EntityBankRow {
  entity_id: string;
  entity_name: string;
  institution_id: string;
  institution_name: string;
  cash_cents: number;
  investment_cents: number;
  debt_cents: number;
  accounts_with_reading: number;
  accounts_total: number;
  oldest_reading_age: number | null;
  all_readings_reported: boolean;
}

/**
 * A saved, named set of entities and accounts. Never an access boundary.
 *
 * A member is either a WHOLE entity (every live account it has, now and
 * later) or ONE account on its own -- Sarah, 2026-09-16: *"I dont see the
 * Morgan stanley accounts avail to add to the groups -- also not credit
 * cards."* Four brokerage accounts under three entities; the entity picker
 * alone could not draw them.
 */
export interface EntityGroup {
  id: string;
  name: string;
  created_at: string;
  created_by: string;
  /** Whole-entity members the VIEWER can see, named. */
  members: { id: string; name: string }[];
  /** Single-account members the VIEWER can see, named. */
  accounts: { id: string; name: string; last4: string | null; entity_id: string; entity_name: string }[];
  entities_total: number;
  accounts_total: number;
  /** `entities_total + accounts_total`. */
  members_total: number;
  /** In the group, out of your reach -- entities and accounts together. */
  members_hidden: number;
}

/** What `POST /api/groups` and `PUT /api/groups/{id}` take. */
export interface GroupBody {
  name: string;
  entity_ids: string[];
  account_ids: string[];
}

export interface ScopedPosition {
  scope: {
    kind: "all" | "group" | "category";
    key?: string;
    label: string;
    members_total: number | null;
    members_visible: number | null;
    members_hidden: number;
    /** How many accounts the figure below was drawn over; null for "all". */
    accounts_visible: number | null;
  };
  position: Dashboard["position"];
  moved: {
    day: string;
    timezone: string;
    in_cents: number;
    out_cents: number;
    internal_cents: number;
    internal_count: number;
    pending_count: number;
    movements: number;
  };
}

/**
 * Available cash for a scope — the components, not a pre-computed total. The
 * screen subtracts `card_cents` and the payables bucket for the chosen horizon
 * from `cash_cents`. `card_cents` is positive when money is owed.
 */
export interface AvailableCash {
  scope: ScopedPosition["scope"];
  cash_cents: number;
  card_cents: number;
  payables: {
    total_cents: number;
    overdue_cents: number;
    /** Cumulative: due by the end of the week, includes overdue. */
    due_7_cents: number;
    /** Cumulative: due by the end of the month, includes the week. */
    due_30_cents: number;
    /** Due within the CONFIGURABLE horizon the request asked for (`?days=`);
     * equals total when the horizon is "All open". */
    due_window_cents: number;
    /** Owed but with no due date yet — in total, in neither horizon. */
    undated_cents: number;
    next_due: string | null;
    items: number;
  };
  /** Expected obligations (059), split by direction and bucketed by horizon the
   * same way as payables — cumulative, so due_30 includes due_7. */
  obligations: {
    built: boolean;
    outflow_total: number;
    outflow_due_7: number;
    outflow_due_30: number;
    /** Out within the configurable horizon; equals total for "All open". */
    outflow_window: number;
    inflow_total: number;
    inflow_due_7: number;
    inflow_due_30: number;
    /** In within the configurable horizon; equals total for "All open". */
    inflow_window: number;
    items: number;
  };
}

/** One uploaded payroll register (#17), as the Payroll page lists it. */
export interface PayrollRegisterRow {
  id: string;
  entity_id: string;
  entity_name: string;
  pay_date: string | null;
  source_filename: string;
  net_pay_cents: number;
  gross_cents: number;
  employee_count: number;
  /** Usable: it parsed, has figures and ties. `parse_error` says why not. */
  tie_ok: boolean;
  parse_error: string | null;
  uploaded_at: string;
  uploaded_by: string | null;
}

/** What the upload endpoint returns for the register just stored. */
export interface PayrollUploadResult {
  id: string;
  uploaded_at: string;
  pay_date: string | null;
  employee_count: number;
  gross_cents: number;
  net_pay_cents: number;
  tie_ok: boolean;
  parse_error: string | null;
}

export interface Dashboard {
  reach: Reach;
  needs_you: {
    /** OPEN cases plus stale readings plus held accounts. Acknowledged ones
     *  are excluded on purpose — see `acknowledged`. */
    total: number;
    breaches: DashboardBreach[];
    stale: DashboardStale[];
    connections: DashboardConnectionProblem[];
    /** Balance moved with no transaction behind it (101). Empty until armed. */
    unreconciled: DashboardUnreconciled[];
    /** Still true, somebody has decided about it, not in `total`. */
    acknowledged: ExceptionCase[];
    case_counts: CaseCounts;
    reopen_margin_pct: number;
    max_snooze_days: number;
    held_for_review: number;
    /** Payables approvals this reader can decide (invoice, payment or
     *  bank-detail change). Counted in `total`; links to /approvals. */
    approvals: number;
    accounts_without_floor: number;
    /** Classification lines the agent was UNSURE about and handed to a person.
     *  Display only, NOT in `total` (a backlog); links to /transactions?tab=review. */
    classification_in_review: number;
    /** New-vendor / unknown-account lines a person must resolve because the
     *  vendor/account isn't known. Display only, NOT in `total`; same link. */
    classification_unknown: number;
    /** AP-inbox invoices that matched no vendor — "an entity invoice the system
     *  doesn't recognise should be an alert" (Sarah 2026-10-28). Counted IN
     *  `total` (a today alert), approver-only (0 otherwise); links to /ap-inbox. */
    ap_unrecognized: number;
  };
  /** Feedback still "open" -- nobody has picked it up. Builder only; 0 for everyone else. */
  feedback_waiting: number;
  position: {
    cash_cents: number;
    investment_cents: number;
    debt_cents: number;
    accounts_with_reading: number;
    accounts_total: number;
    /** Of `accounts_total`, how many are cash accounts. */
    cash_accounts: number;
    oldest_reading_age: number | null;
    by_entity: PositionRow[];
    by_institution: PositionRow[];
    /** Entity → its banks (059). Present from `/api/position/scoped`; the
     * dashboard's own position may omit it. */
    by_entity_bank?: EntityBankRow[];
    /**
     * What the cash line is carrying that no bank reported — the hand-set
     * available slices inside brokerage accounts (071), grouped by bank, so the
     * Cash tile can say WHERE the extra cash is and WHEN it was last confirmed.
     * Empty for every book that has set none. `as_of` is the oldest in the group.
     */
    manual_cash: { institution_name: string; cents: number; as_of: string }[];
  };
  coverage: DashboardCoverage;
  poller: Poller;
  feeds: Feeds;
  backup: Backup;
}

/**
 * One sweep of the balance poller, as `schema/012_poller_run.sql` records it.
 *
 * `dry_run` is the field that matters most and reads like a detail. A dry
 * sweep proves the loop is alive and calls no bank, so every number beside it
 * can look healthy while nothing on the dashboard has been refreshed.
 */
export interface PollerRun {
  started_at: string;
  finished_at: string | null;
  dry_run: boolean;
  /** Accounts past their staleness threshold when the sweep started. */
  due: number;
  calls: number;
  refreshed: number;
  failed: number;
  capped: boolean;
  error: string | null;
}

/** `last_good` carries only what proves it: when, and how much it moved. */
export interface PollerGoodRun {
  started_at: string;
  refreshed: number;
  failed: number;
}

/**
 * Whether the thing that keeps these numbers current is actually running.
 *
 * `last_good` is separate from `last` deliberately, and the separation is the
 * whole point: `api/routes/dashboard.py` states it as "a dry run proves the
 * loop is alive and proves nothing about whether the balances on this screen
 * are current. Only a live, error-free sweep does that." A UI that collapses
 * the two reads an unarmed deployment sweeping harmlessly every fifteen
 * minutes as a healthy one -- which is exactly the failure of 2026-08-27,
 * rebuilt somewhere new.
 *
 * `last_good` is NULL on a quiet system, and that is correct rather than a
 * gap: no evidence and evidence of health must not render as the same thing.
 */
export interface Poller {
  enabled: boolean;
  interval_seconds: number;
  last: PollerRun | null;
  last_good: PollerGoodRun | null;
}

/**
 * One run of a transaction feed, narrowed to what the dashboard band needs.
 *
 * ⚠️ Deliberately NOT `SyncRun` above, which is the whole row and carries
 * `login_label`. This one is rendered for every signed-in user, and a login
 * label names an institution somebody may have no grant to see -- the same
 * rule `/api/connections/coverage` follows. Counts and timestamps only.
 *
 * `trigger` is the field that looks like metadata and is not. 'ui' and 'cli'
 * are a person; only 'schedule' is the timetable. A feed kept current by
 * somebody pressing a button is a feed with no automation behind it, and on
 * 2026-09-04 that distinction was the entire diagnosis.
 */
export interface FeedRun {
  feed: SyncFeed;
  trigger: "ui" | "cli" | "schedule";
  started_at: string;
  /** Null while any login in the sweep is still in flight. */
  finished_at: string | null;
  added: number | null;
  changed: number | null;
  /**
   * How many logins the figures above cover. A sweep writes one row per
   * login, so `last_scheduled` is summed across them and this is the count it
   * was summed over -- without it, "0 new" cannot be told apart from "0 new
   * on the one login I happened to read".
   */
  logins: number;
  /** Logins in this sweep that ended on an error. */
  failed: number;
  error: string | null;
}

/**
 * Whether anything is pulling the transaction feeds on its own.
 *
 * The sibling of `Poller`, and it exists because that one covers BALANCES
 * ONLY. On 2026-09-04 the balances were current and the transactions were four
 * days stale — because a person had been refreshing balances by hand while the
 * transaction timetable sat unarmed, firing dry runs that call nothing and
 * report no error. Two mechanisms, two switches, and until now one band.
 *
 * `last_scheduled` is separate from `last` for exactly the reason `last_good`
 * is separate from `last` on the poller: it is the only one that is evidence
 * of automation rather than of somebody's attention.
 */
export interface Feeds {
  enabled: boolean;
  /** Local times the timetable fires, e.g. "06:00,13:00". */
  times: string;
  timezone: string;
  feeds: string[];
  /** The slot the timetable is owed, or null when it is up to date. */
  due: string | null;
  /** A misconfiguration that stops the schedule firing at all. */
  problem: string | null;
  last: FeedRun | null;
  last_scheduled: FeedRun | null;
}

/**
 * One attempt of the nightly backup, as `schema/017_backup_run.sql` records
 * it and 041 extends it.
 *
 * `verified` is the field that matters most and reads like a detail: pg_dump
 * exits zero on a dump missing a table it never knew about, so `error: null`
 * is not "we have a backup". Only `verified: true` is. And `offsite_name` is a
 * third, separate fact -- a verified dump on the host is lost with the host.
 */
export interface BackupRun {
  started_at: string;
  finished_at: string | null;
  slot: string | null;
  trigger: "schedule" | "cli";
  dry_run: boolean;
  dump_name: string | null;
  dump_bytes: number | null;
  secrets_files: number | null;
  verified: boolean;
  notes: string | null;
  pruned: number;
  free_mb: number | null;
  error: string | null;
  /** The object key in the bucket. Null when the set never left the host. */
  offsite_name: string | null;
  /** Why a verified dump did not reach the bucket. Null when it did, or when
   *  nothing was owed (leg off, or the dump did not verify). */
  offsite_error: string | null;
}

/** `last_good` carries only what proves it: when, what, and whether it left. */
export interface BackupGoodRun {
  started_at: string;
  dump_name: string;
  dump_bytes: number;
  free_mb: number | null;
  offsite_name: string | null;
}

/**
 * Whether the book is backed up, and whether the backup would survive losing
 * the box. Two switches, kept apart in the payload for the reason
 * `api/backup.health` gives: `enabled` decides whether a dump is written,
 * `offsite.enabled` whether it travels, and a reader needs both answers.
 */
export interface Backup {
  enabled: boolean;
  /** Local time the slot fires, e.g. "02:30". */
  time: string;
  timezone: string;
  offsite: {
    enabled: boolean;
    /** All four R2_* values present. Armed and unconfigured is a fault. */
    configured: boolean;
  };
  last: BackupRun | null;
  last_good: BackupGoodRun | null;
}

/** What `POST /api/sync/backup` says when a dump was written. */
export interface BackupNow {
  dump_name: string;
  dump_bytes: number;
  secrets_files: number;
  verified: boolean;
  notes: string | null;
  free_mb: number | null;
  offsite_name: string | null;
  offsite_error: string | null;
}

export interface RefreshResult {
  refreshed: number;
  failed: number;
  skipped: number;
  notes: string[];
}

export interface Transaction {
  id: string;
  plaid_transaction_id: string;
  /**
   * Statement convention: POSITIVE means money came IN, negative means it went
   * OUT. This is FLIPPED from what Plaid sends — the flip happens once, in
   * `api/plaid_client.py`. `sum(amount_cents)` is the change in the balance.
   */
  amount_cents: number;
  /** What Plaid actually said, unflipped, so the conversion stays checkable. */
  plaid_amount_cents: number;
  currency: string | null;
  name: string;
  merchant_name: string | null;
  posted_on: string;
  authorized_on: string | null;
  pending: boolean;
  payment_channel: string | null;
  category: string[] | null;

  /**
   * The other party, read out of `name` by the rules in api/counterparty.py.
   * NULL means no rule matched — a countable gap, not a silent guess.
   */
  counterparty_name: string | null;
  /**
   * external — someone outside the group
   * internal — money moving between VGH's own accounts. Not spend. This is
   *            the largest single category in the data by value.
   * check    — a cheque. The bank does not carry the payee; QuickBooks does.
   * deposit  — money in, source not stated by the bank.
   * unknown  — no rule matched.
   */
  counterparty_kind:
    | "external"
    | "internal"
    | "check"
    | "deposit"
    | "unknown"
    | null;
  /** Which rule produced the name, so a wrong one is traceable in bulk. */
  extraction_rule: string | null;

  /** The matching movement on the other account was actually found. */
  internal_pair_found: boolean;
  /** NULL when the mask was ambiguous — we know it went inside, not where. */
  internal_account_name: string | null;
  internal_account_last4: string | null;

  /**
   * The counterparty's NAME matches one of the group's own entities. A
   * resemblance, NOT a fact — render it as a question. `null` on
   * `related_party_confirmed` means nobody has ruled on it yet, which is not
   * the same as `false` ("looked, and it really is an outside party").
   */
  related_entity_name: string | null;
  related_party_confirmed: boolean | null;

  first_seen_at: string;
  last_changed_at: string;
  removed_at: string | null;
}

export interface TransactionSummary {
  /** False means we have never pulled transactions here — not "nothing moved". */
  has_any: boolean;
  transactions: number;
  pending: number;
  earliest: string | null;
  latest: string | null;
  net_cents: number;
  money_in_cents: number;
  money_out_cents: number;
  /**
   * When this feed was last PULLED — not when the newest transaction in it
   * happened. Null means never. Balance age is shown on every screen here;
   * this was shown on none, and the two have run more than a day apart.
   */
  synced_at: string | null;
}

/**
 * One row of brokerage activity.
 *
 * A separate type from `Transaction`, and deliberately not an extension of
 * it. They come from different Plaid products with different id spaces, and
 * most rows here are not money moving anywhere — 1,574 of 2,331 on the real
 * book are dividends. Treating them as the same shape is how internal churn
 * ends up in a total that means "money that moved".
 */
export interface InvestmentTransaction {
  id: string;
  plaid_investment_transaction_id: string;
  /**
   * Statement convention: POSITIVE means cash came IN. Flipped from Plaid,
   * once, at the boundary — the same direction as `Transaction`, but proven
   * separately for this feed rather than assumed from that one.
   */
  amount_cents: number;
  plaid_amount_cents: number;
  fees_cents: number | null;
  currency: string | null;
  /** Plaid's own class: cash, buy, sell, fee, transfer. */
  txn_type: string;
  /** dividend, withdrawal, deposit, buy, sell… Can be an empty string. */
  txn_subtype: string | null;
  name: string;
  security_id: string | null;
  quantity: number | null;
  price_cents: number | null;
  /** When it hit the account. What a statement reconciles against. */
  posted_on: string;
  /**
   * When it actually happened, which is sometimes months earlier. A DATE, not
   * a timestamp: the broker gives a day and Plaid dresses it as midnight UTC,
   * which read back as the previous day in New York.
   */
  effective_on: string | null;
  /**
   * Cash actually entered or left. NOT the same as `txn_type === "cash"` —
   * that includes every dividend and overcounts movement about twentyfold.
   */
  moves_cash: boolean;
  cancelled_at: string | null;
  first_seen_at: string;
  last_changed_at: string;
}

export interface InvestmentSummary {
  /** False means no brokerage activity has been pulled — not "nothing moved". */
  has_any: boolean;
  /** Everything, dividends and trades included. */
  transactions: number;
  /** Only the rows where cash entered or left. */
  cash_movements: number;
  earliest: string | null;
  latest: string | null;
  /**
   * ⚠️ Cash movements only. This is NOT the change in the account's value — a
   * brokerage account whose holdings fell by a million has a net cash figure
   * of zero, and both facts are true. Never label it "change".
   */
  net_cash_cents: number;
  cash_in_cents: number;
  cash_out_cents: number;
  /** When the broker feed was last pulled. Nothing refreshes it on a schedule. */
  synced_at: string | null;
}

export interface PositionPoint {
  /** A date, in the reporting timezone. Not a timestamp. */
  day: string;
  /** Cash accounts only — no cards, no brokerage. See PositionRow. */
  cash_cents: number;
  /** How many accounts this total was built from. Never render one without it. */
  accounts_with_reading: number;
  accounts_total: number;
}

export interface PositionHistory {
  days: number;
  timezone: string;
  points: PositionPoint[];
  /**
   * True when the number of accounts underneath the line changes across the
   * window, so the two ends are not measuring the same book. The page must
   * say so — a rise that is really "Chase got linked" reads as growth.
   */
  completeness_changed: boolean;
  accounts_at_start: number;
  accounts_at_end: number;
  accounts_total: number;
}

export interface RecordedReading {
  /** False when this exact reading was already stored — a double-submit. */
  recorded: boolean;
  observation: Observation;
  /**
   * Whether this reading is now the balance the dashboard shows. False when a
   * fresher reading already exists — backdating a statement does not displace
   * this morning's bank number, and the screen has to say so rather than
   * implying the typed figure won.
   */
  is_current: boolean;
  account_name: string;
}

/** One account's outcome inside a batch of typed balances. */
export interface RecordedReadingResult {
  account_id: string;
  account_name?: string;
  /** True when this row was written (or already present); false when refused. */
  ok: boolean;
  /** present when `ok` — false means this exact reading was already stored. */
  recorded?: boolean;
  /** present when `ok` — whether this is now the balance the dashboard shows. */
  is_current?: boolean;
  balance_cents?: number;
  /** present when `!ok` — why this row was not written. */
  error?: string;
}

export interface RecordedReadings {
  results: RecordedReadingResult[];
  /** How many rows were newly written (double-submits do not count). */
  recorded: number;
  failed: number;
}

// ---------------------------------------------------------------------------
// The calls themselves.
// ---------------------------------------------------------------------------
/**
 * Who the money moves with.
 *
 * `gross_cents` is in + out, and it is what the list is ranked by. Net is the
 * wrong sort order for this question: a party billed $2m and refunded $2m nets
 * to nothing and is not nothing.
 *
 * `out_cents` is POSITIVE for money that left -- the sign flip happens in SQL
 * so nothing on this side has to remember it.
 */
export interface Counterparty {
  counterparty_name: string;
  counterparty_kind: string;
  transactions: number;
  in_cents: number;
  out_cents: number;
  net_cents: number;
  gross_cents: number;
  first_seen: string;
  last_seen: string;
  accounts: number;
}

export interface CounterpartyTotal {
  counterparty_kind: string | null;
  transactions: number;
  gross_cents: number;
  /** NULL when the kind carries no names at all -- every internal transfer is
   *  matched by account mask. Render it as "--", never as zero. */
  parties: number | null;
}

/**
 * Movement between our own accounts.
 *
 * `pair_found` is the difference between evidence and inference, and the
 * screen is required to show it: true means the opposite leg was actually
 * found on the other account, reported independently by a different bank.
 * False means a four-digit mask matched, and several accounts can share one.
 */
export interface InternalTransfer {
  id: string;
  posted_on: string;
  amount_cents: number;
  name: string;
  counterparty_name: string | null;
  pair_found: boolean;
  from_account: string;
  from_last4: string | null;
  from_entity: string;
  to_account: string | null;
  to_last4: string | null;
  to_entity: string | null;
}

/** A counterparty name resembling one of our own entities. A question. */
export interface RelatedParty {
  entity_name: string;
  counterparty_name: string;
  transactions: number;
  gross_cents: number;
  first_seen: string;
  last_seen: string;
}

/** One row of the cross-account drill-down. */
export interface FoundTransaction {
  id: string;
  posted_on: string;
  amount_cents: number;
  name: string;
  pending: boolean;
  counterparty_name: string | null;
  counterparty_kind: string | null;
  pair_found: boolean;
  resembles_our_own: boolean;
  account_id: string;
  /**
   * ⚠️ THE BANK ACCOUNT THE MONEY MOVED THROUGH — not what it was booked to.
   * `classified_account` below is the expense or income account. Freund asked
   * for the second and this column was showing the first (T1).
   */
  account_name: string;
  last4: string | null;
  /**
   * ⚠️ #8: the KIND of bank account (business_checking, credit_card, …), used
   * only to name the QuickBooks transaction type the proposal would post as (a
   * charge on a card, a check or a deposit on a bank). Display only.
   */
  bank_account_type: string | null;
  entity_id: string;
  entity_name: string;

  /**
   * Where this transaction has got to on the way into QuickBooks.
   *
   * ⚠️ NOTHING TO DO WITH `pending`, which is Plaid's settlement flag and
   * clears on its own in about two days. A row can be `pending` and `posted`
   * at once — two independent axes, and the screen must never render one as
   * the other. Before 2026-09-23 there was no state but `pending`, which is
   * why every row on the screen read the same.
   *
   * `unclassified` is the absence of a decision rather than a stored value:
   * nothing has had an opinion about this row yet.
   */
  state:
    | "unclassified"
    | "proposed"
    | "in_review"
    | "reviewed"
    | "posted"
    | "excluded";
  /** 0..1, or null when nothing scored it — which is not a low score. */
  confidence: number | null;
  excluded_reason:
    | "already_in_quickbooks"
    | "not_a_posting"
    | "duplicate_feed_row"
    | null;
  /** The QuickBooks account it was booked to, `Parent:Sub` as the chart holds it. */
  classified_account: string | null;
  /** That account's type (asset/liability/income/expense…), carried from the
   *  chart — "classify by account, not by type"; the type comes with it. */
  classified_account_type: string | null;
  classified_account_qbd_id: string | null;
  /**
   * ⚠️ #10.2: the categorisation lines of the matched QuickBooks document, so a
   * split (payroll net pay, a multi-account bill) shows every line, not only the
   * biggest. Empty for an unmatched row. `amount` is a string (dollars) to keep
   * the numeric exact.
   */
  classified_splits: Array<{
    full_name: string;
    account_type: string | null;
    amount: string;
  }>;
  classified_file_id: string | null;
  /**
   * ⚠️ #1: the company file this line's account MAPS to (entity → company
   * file), so the classify dialog opens with that file FIXED rather than asking
   * the operator to pick it. Null only when the entity has no file — which is
   * exactly when the row is not `classifiable` and has no Classify action.
   */
  preset_file_id: string | null;
  preset_file_name: string | null;
  posted_qbd_transaction_id: string | null;
  /** ⚠️ false means the manual Pay Bills link is OWED; null means N/A. */
  pay_bills_linked: boolean | null;
  /**
   * Who decided, and why. `agent` is the classifier
   * (`api/classification.py`); `person` is somebody by hand. Null until
   * anything has an opinion. The review screen reads the two below so an
   * agent's unsure row arrives with the accounts the books actually used.
   */
  decided_how: "agent" | "person" | null;
  /** Who signed off and when — populated once a PERSON decided (approve / a
   *  by-hand classify). Drives the "Reviewed by X · date" line in the drawer;
   *  null while the engine owns the row or nothing has an opinion yet. */
  decided_by_name: string | null;
  decided_at: string | null;
  /** The engine's tier — what it would do on its own. The status badge labels
   *  off this, not decided_how: a person-tier row is never shown as "auto". */
  tier: "auto" | "one_click" | "person" | null;
  /** The accounts history offered, when the agent could not be sure. */
  candidates: Array<{
    qbd_id: string;
    full_name: string | null;
    confidence: number;
    because: string;
  }>;
  /** Why the agent decided as it did, in prose. */
  matched_on: string | null;
  /**
   * ⚠️ Whether this row is a question at all. False for a bank account whose
   * entity has no QuickBooks file: shown, but never queued and never asked
   * about (Sarah, 2026-10-20). The screen drops the Classify affordance on a
   * row that is not classifiable rather than inviting a decision that scope
   * says should not be made.
   */
  classifiable: boolean;
}

/**
 * How many rows sit behind each tab, over the same search and window.
 *
 * ⚠️ Counted WITHOUT the tab filter. Counting them through it would show the
 * open tab's number beside two zeroes, which reads as "there is nothing to
 * review" on a screen whose entire job is to say how much there is.
 */
export interface TransactionTabs {
  review: number;
  categorized: number;
  excluded: number;
  unclassified: number;
  in_review: number;
  posted: number;
}

export interface TransactionSearch {
  total: number;
  /**
   * The OLDEST sync across every login in reach, because that is what bounds
   * the honesty of the whole result set. Null means at least one reachable
   * login has never been synced — a stronger statement than "old".
   */
  feed_synced_at: string | null;
  /**
   * ⚠️ EXTERNAL ONLY, since 2026-09-07. Money that crossed the boundary of
   * this book — transfers between accounts it owns are in the `internal_*`
   * figures below, not in these.
   *
   * Positive for money that left, like every other out-figure in this API.
   */
  out_cents: number;
  in_cents: number;
  /**
   * One sum, seen twice: the same money leaving one of these accounts and
   * arriving in another. It used to be added to BOTH figures above at full
   * value, which is most of why Sarah reported the transactions screen showing
   * "very high numbers" — on the live feed it was 28% of each side.
   *
   * Reported rather than dropped. The movement happened and is in the feed;
   * a screen that hid it would be a different kind of wrong.
   */
  internal_out_cents: number;
  internal_in_cents: number;
  internal_count: number;
  /**
   * ⚠️ A SUBSET of the figures above, never an addition to them. A pending
   * row is already counted in `in_cents`/`out_cents`; these repeat it so the
   * screen can say how much of a day has not settled. Adding them to the
   * headline double-counts.
   *
   * `pending_count` spans every matching row. The two cent figures are
   * EXTERNAL ONLY, matching the tiles they sit under.
   *
   * ⚠️ `TransactionSummary` above (the account detail page) does the
   * OPPOSITE and excludes pending entirely. Different questions -- "what has
   * settled on this account" against "what moved today" -- but the two will
   * not agree, and each screen now says which one it is answering.
   */
  pending_count: number;
  pending_in_cents: number;
  pending_out_cents: number;
  /**
   * ⚠️ `total` counts the ROWS. This counts the movements behind the MONEY
   * figures, which cover a different span by default. Two counts of two
   * questions, named apart on purpose — collapsing them is how a screen starts
   * quietly misreporting one of them.
   */
  summary_movements: number;
  /** The span the money figures actually cover, echoed back so the tiles can
   *  say so rather than the reader inferring it from a filter box. */
  summary_since: string | null;
  summary_until: string | null;
  limit: number;
  offset: number;
  error?: string;
  transactions: FoundTransaction[];
  /** How many rows each tab holds, over the same search. See the type. */
  tabs: TransactionTabs;
}

export interface TransactionFilters {
  /** Read-only union of e:entity and a:account IDs; none means empty. */
  selection?: string;
  entity_id?: string;
  account_id?: string;
  /**
   * The precise filter in the Filters panel: a contains-match on the
   * counterparty name and nothing else.
   *
   * ⚠️ NOT the box at the top of the screen. That is `search` below, and the
   * difference is the whole of G2 — this field WAS the top box until
   * 2026-09-23, which is why Freund could not find a transaction by its
   * amount and went to his browser's find bar instead.
   */
  counterparty?: string;
  kind?: string;
  q?: string;
  /**
   * The one box at the top of the screen: every column at once — counterparty,
   * description, account, entity, date and amount.
   *
   * Amounts may be typed the way people write them. `13,956.88`, `$13,956.88`
   * and `13956.88` are the same search; the server strips the separators and
   * also matches the figure as it is rendered, so a partial `956.88` lands too.
   */
  search?: string;
  /**
   * ⚠️ SORTING IS THE SERVER'S JOB ON THIS ENDPOINT, because it returns a page
   * out of a total. Sorting the rows after they arrive orders one page and
   * presents it as the order of the table — which looks right and is not.
   * One of: date, counterparty, entity, account, amount, description.
   */
  sort?: string;
  dir?: "asc" | "desc";
  /** review | categorized | excluded — the three tabs. */
  tab?: string;
  /** The finer filter underneath `tab`, when one state is wanted on its own. */
  state?: string;
  min_dollars?: string;
  max_dollars?: string;
  since?: string;
  until?: string;
  direction?: string;
  /**
   * The MONEY figures' own window, separate from the rows'. Omitted, they
   * follow `since`/`until`. The transactions screen sends today here while
   * leaving the rows on the whole feed — "what moved today" and "find this
   * vendor's payments" are two questions and one window cannot serve both.
   */
  summary_since?: string;
  summary_until?: string;
  /** A saved group's id, or a category key. Narrows rows AND money figures. */
  group?: string;
  category?: string;
  limit?: number;
  offset?: number;
}


// ---------------------------------------------------------------------------
// The books. QuickBooks Desktop, through Conductor, read-only.
//
// ⚠️ **Money in this block is a decimal STRING, not integer cents.** Every
// other type in this file follows the note at the top -- integer cents,
// nothing divides. These do not, and the difference is deliberate rather than
// an inconsistency to tidy up: the QuickBooks columns are `numeric(18,2)`
// because QuickBooks reports amounts as decimal strings, and `float("0.10")`
// is not 0.10. Render them with `formatLedgerAmount`, never `formatCents`, and
// never put one through `Number()`.
//
// ⚠️ There is no `cleared_status` here and there must not be one yet. It is
// NULL on every row: the flag is readable, but only off a per-bank-account
// report that is not built, not off the transaction pull these types describe.
// Inferring "not cleared" from NULL would ship it wrong. And when it does
// land: one transaction can carry both Cleared and NotCleared lines, so it is
// a per-line value, never a per-transaction one.
// ---------------------------------------------------------------------------

export interface BookFile {
  id: string;
  name: string;
  /** Bookview has connected this file to Conductor. Nothing syncs without it. */
  connected: boolean;
  /** Held out of the whole-book sweep (nightly / every-file); runs only when
   * named. For a file mid-backfill whose crash would block the others (079). */
  hold_from_sweep: boolean;
  connected_at: string | null;
  /** Every entity on the file. All of them are visible, or the file is not. */
  entities: string[];
  account_count: number;
  inactive_count: number;
  trial_balance_dates: number;
  last_sync_at: string | null;
}

/**
 * A payment that is out of pattern for who it went to (H4).
 *
 * (!) A PROMPT TO LOOK, NEVER A FINDING. Nothing behind this knows whether a
 * payment is wrong; it knows the payment is unlike its neighbours. The screen
 * has to say so in those words, because "flagged" reads as "faulty".
 */
export interface UnusualPayment {
  id: string;
  posted_on: string;
  pending: boolean;
  amount_cents: number;
  name: string | null;
  counterparty_name: string;
  account_id: string;
  account_name: string;
  last4: string | null;
  entity_name: string;
  /** The counterparty's median payment, over `history` earlier payments. */
  usual_cents: number;
  /**
   * The most this counterparty had ever been paid before this one. A flagged
   * payment is by definition above it -- showing both is what lets a reader
   * judge the flag rather than take it on trust.
   */
  most_cents: number;
  history: number;
  times_usual: number;
}

export interface UnusualAmounts {
  days: number;
  /** Payments in the window that were looked at at all. */
  considered: number;
  flagged: number;
  /**
   * (!) PART OF THE ANSWER, not a diagnostic. An empty list means "nothing was
   * unusual" OR "nothing could be assessed", and only this number tells them
   * apart -- the first month of any new bank connection is entirely the
   * second. A screen that reported "all clear" for that would be making a
   * claim nobody has checked.
   */
  could_not_judge: number;
  transactions: UnusualPayment[];
  truncated: boolean;
  /** The rule itself, so the screen states it rather than paraphrasing it. */
  rule: {
    min_history: number;
    multiple: number;
    /** Always true: a payment inside what has already happened is not news. */
    above_previous_largest: boolean;
    floor_dollars: number;
    baseline_days: number;
  };
}

export interface LedgerAccount {
  /** The only join key. `full_name` is a display path and is not unique. */
  qbd_id: string;
  name: string;
  full_name: string;
  parent_full_name: string | null;
  account_number: string | null;
  account_type: string | null;
  special_account_type: string | null;
  /** Inactive accounts are listed. They still carry balances. */
  is_active: boolean;
  balance: string | null;
  total_balance: string | null;
  last_seen_at: string;
  file_id: string;
  file_name: string;
  /**
   * Which of the six sections of a set of books this account sits in --
   * "asset", "liability", "equity", "income", "expense" or "other". Decided on
   * the server from the QuickBooks account type, so there is one definition of
   * "is this an asset" rather than one per screen. It is the top level of the
   * account tree (R5).
   *
   * (!) "other" is a real answer and is SHOWN as its own branch. A type the
   * server's map has never seen lands there rather than being dropped -- a
   * tree missing a branch looks exactly like a tree that is complete.
   */
  group: "asset" | "liability" | "equity" | "income" | "expense" | "other";
  /**
   * Lines posted to this account, narrowed by the `since`/`until` passed to
   * `bookAccounts` -- and NOT the same number as `documents`. A four-line bill
   * is four lines and one document.
   *
   * (!) These narrow the COUNTS only. `balance` is a running total as at the
   * last sync and no date window touches it.
   */
  lines: number;
  documents: number;
}

/**
 * One LINE of one QuickBooks transaction. Not one transaction.
 *
 * ⚠️ The unified pull returns a row per line and `020_qbd_transaction_identity`
 * makes `(qbd_transaction_id, qbd_line_id)` the identity, so a four-line bill
 * arrives as four of these sharing a `qbd_transaction_id`. Anything counting
 * these and calling the result a transaction count is wrong by however many
 * lines the book's documents happen to carry.
 *
 * ⚠️ `account_full_name` is null when the account has not been pulled yet, and
 * that is a real state rather than a defect: accounts and transactions are two
 * independent Conductor pulls and 018 deliberately does not make this a
 * foreign key. Render the id, never hide the row.
 */
export interface LedgerTransaction {
  qbd_transaction_id: string;
  /** Null is a real line id, not a missing one. The index treats it as ''. */
  qbd_line_id: string | null;
  /** The raw QuickBooks type (`credit_card_charge`) — used for filtering. */
  transaction_type: string;
  /** The same type as a reader should see it ("Credit Card Charge"), computed
   *  on the server so the screen and every export read the same words (#1). */
  transaction_type_label: string;
  transaction_date: string | null;
  /** A1: coalesced from the transaction's header row when the line itself
   *  carries none (ref # and the customer/vendor name live on the header). */
  ref_number: string | null;
  memo: string | null;
  entity_ref_name: string | null;
  account_qbd_id: string | null;
  /** The account(s) on the other side of the transaction, or "-SPLIT-" for
   *  more than one — QuickBooks' Split column (A3). Null when there is none. */
  split_account: string | null;
  /** The cumulative total down the account in date order (stored sign; the
   *  screen flips income positive like the line amount). Only on the
   *  single-account drill-down; null for a multi-account search. */
  running_balance: string | null;
  /** Decimal STRING. Never `Number()` it — see the note above this block. */
  amount: string | null;
  currency: string | null;
  amount_home: string | null;
  qbd_updated_at: string | null;
  pulled_at: string;
  /** Null when no pulled account carries `account_qbd_id`. */
  account_full_name: string | null;
  account_name: string | null;
  account_is_active: boolean | null;
  file_id: string;
  file_name: string;
}

/**
 * One account's figure on a profit and loss.
 *
 * ⚠️ `amount` is the PRESENTED figure — income flipped positive because that
 * is how a reader expects to see it. `signed_total` is the raw sum of the
 * lines, which for income is negative because income arrives as credits. Both
 * go out so that anybody checking this against QuickBooks can see what was
 * added up before it was flipped.
 */
export interface ProfitLossPart {
  file_id: string;
  file_name: string | null;
  /** Unique inside ITS OWN FILE and nowhere else. */
  qbd_id: string;
  lines: number;
  documents: number;
  signed_total: string;
  amount: string;
}

export interface ProfitLossLine {
  /**
   * ⚠️ NULL when this line was merged across more than one company file. A
   * QuickBooks list id is unique inside its own file and nowhere else, so a
   * combined line has no single id to drill into — it has `files`, one entry
   * per file, each carrying the id that does work there. A screen that assumed
   * this was always a string would build a link to the wrong account.
   */
  qbd_id: string | null;
  /** The file `qbd_id` belongs to; null for the same reason. */
  file_id: string | null;
  full_name: string;
  parent_full_name: string | null;
  /** Two files calling the same name different types shows both, "a / b". */
  account_type: string | null;
  lines: number;
  documents: number;
  /** Decimal STRING. Never `Number()` it — see the note above this block. */
  signed_total: string;
  amount: string;
  /**
   * Which statement section this line sits in (QuickBooks account type, round-3
   * #8): `income`, `cogs`, `expenses`, `other_income` or `other_expense`. Null
   * for a line that is not a P&L account.
   */
  section: string | null;
  /** One entry per file this line was added up from. Never empty. */
  files: ProfitLossPart[];
}

/**
 * Round-3 #5: the P&L (and, later, the Balance Sheet) as COLUMNS. A column is a
 * period ($ you can drill into, carrying its own dates) or a derived delta ($ or
 * % change in a comparison). Each line and subtotal carries a value per column
 * key; a period/delta_abs value is a money decimal string, a delta_pct value is
 * a plain percent number string ("12.3") or "" when there is no base.
 */
export interface PLColumnDef {
  key: string;
  label: string;
  kind: "period" | "delta_abs" | "delta_pct";
  /** Period columns only: the dates a cell drills into. */
  since?: string | null;
  until?: string | null;
}

export interface PLColumnLine {
  full_name: string;
  parent_full_name: string | null;
  /** Null when combined across files (no single account to drill into). */
  qbd_id: string | null;
  file_id: string | null;
  section: string;
  values: Record<string, string>;
}

export interface PLColumnSection {
  key: string;
  label: string;
  lines: PLColumnLine[];
  subtotals: Record<string, string>;
}

export interface PLColumns {
  defs: PLColumnDef[];
  /** "months" | "quarters" | "years" | "total" | "compare". */
  mode: string;
  sections: PLColumnSection[];
  income_total: Record<string, string>;
  gross_profit: Record<string, string>;
  net_operating_income: Record<string, string>;
  net: Record<string, string>;
  has_cogs: boolean;
  has_other: boolean;
}

/**
 * One section of the P&L statement (QuickBooks account type, round-3 #8): its
 * lines and subtotal. `key` is one of income / cogs / expenses / other_income /
 * other_expense; the server returns all five in order and the screen draws the
 * ones that have lines. The file's own top-level expense parents are NOT
 * sections — they are the tree groups inside the one Expenses section.
 */
export interface ProfitLossSection {
  key: string;
  label: string;
  lines: ProfitLossLine[];
  subtotal: string;
}

/**
 * One company file's own totals — the per-entity cut of the same report.
 *
 * ⚠️ `partial` means this file carries books belonging to entities the chosen
 * group did NOT ask for, and its figures therefore include them. A company
 * file cannot be cut by entity (the same reason a trial balance cannot), so
 * the report says so rather than quietly answering a wider question. The
 * screen has to show it.
 */
export interface ProfitLossFile {
  file_id: string;
  name: string;
  entities: string[];
  entities_total: number;
  entities_in_scope: number;
  partial: boolean;
  income_total: string;
  expense_total: string;
  net: string;
  unclassified_accounts: number;
  documents: number;
  lines: number;
}

/**
 * Income and expense over a range.
 *
 * ⚠️ **`basis: "lines_pulled"` means this was COMPUTED FROM THE TRANSACTION
 * LINES in our database. It is not QuickBooks' own P&L report**, and the two
 * can disagree. The screen has to say so, and every line has to drill into the
 * rows behind it, so a disagreement can be found rather than argued about.
 *
 * ⚠️ `unclassified` is not a leftovers bin to hide. It is every account whose
 * type this report did not recognise, and it is shown: a P&L that is silently
 * short looks exactly like one that is right.
 */
export interface ProfitLoss {
  file_id: string | null;
  files: BookFile[];
  since: string | null;
  until: string | null;
  income: ProfitLossLine[];
  expense: ProfitLossLine[];
  unclassified: ProfitLossLine[];
  income_total: string;
  expense_total: string;
  net: string;
  /**
   * The statement grouped into sections, with Gross Profit (Chaim 2026-10-21).
   * `gross_profit` is Sales (operating income) − COGS; `has_cogs` is false when
   * the file has no COGS accounts with activity, and the Gross Profit line is
   * then hidden rather than duplicating Income. Net is unchanged and still
   * reconciles to income_total − expense_total.
   */
  sections: ProfitLossSection[];
  sales: string;
  cogs_total: string;
  gross_profit: string;
  has_cogs: boolean;
  /**
   * Net Operating Income = Gross Profit − operating Expenses (round-3 #8).
   * Drawn between the Expenses section and Other Income / Other Expense, but
   * only when `has_other` — otherwise it equals `net` and the screen draws Net
   * income alone rather than two identical bottom lines.
   */
  net_operating_income: string;
  has_other: boolean;
  /**
   * Round-3 #5: the columnar view — by month/quarter/year with a Total, or a
   * two-period comparison with $ and % change. Null in the default single-range
   * report; present only when the screen asks for columns or a comparison, so
   * the ordinary P&L above is untouched.
   */
  columns: PLColumns | null;
  /** The same report, one row per company file. "Per entity". */
  by_file: ProfitLossFile[];
  /** How many of `by_file` are wider than the question. Zero is the good case. */
  partial_files: number;
  /** What was asked for, and how much of it the reader could not be shown. */
  scope: {
    kind: "all" | "group" | "category";
    key?: string;
    label: string;
    members_total: number | null;
    members_hidden: number;
  };
  /** B2: the latest tie-out verdict, only for a single-file P&L (a combined
   *  report has no one trial balance to tie to). Null otherwise. */
  tieout?: LedgerTieout | null;
  basis: "lines_pulled";
}

/**
 * Ask (R4) — one line of English, an account and a period, a figure.
 *
 * ⚠️ **`interpretation` IS NOT DECORATION AND THE SCREEN MUST PRINT IT.** This
 * box invites a question in English and answers with a number, so the failure
 * mode is not "no answer" — it is a confident answer to a question nobody
 * asked, for the wrong account or the wrong August, indistinguishable from the
 * right one. Showing what was understood beside the figure is what makes a
 * misreading visible in the same glance as the number.
 *
 * ⚠️ **`matched` AND `total` ARE DIFFERENT FACTS.** No account by that name,
 * and an account with no activity, both give a total of zero and they mean
 * opposite things — "we have no such account" versus "we spent nothing". Read
 * `matched` before you render a zero.
 */
export interface AskAccount {
  file_id: string;
  file_name: string | null;
  qbd_id: string;
  full_name: string;
  account_type: string | null;
  /** "income", "expense", or null for an account that is on neither side. */
  side: "income" | "expense" | null;
  lines: number;
  documents: number;
  signed_total: string;
  amount: string;
}

export interface AskAnswer {
  question: string;
  interpretation: {
    /** What it actually searched account names for. */
    needle: string;
    /** The period in words, always — never blank. */
    period: string;
    since: string | null;
    until: string | null;
    understood: { term: string; means: string }[];
  };
  /** How many accounts matched the name, before any limit. */
  matched: number;
  accounts: AskAccount[];
  /**
   * ⚠️ Null when the matches are not all the same side, and null when the list
   * was truncated. Adding income to expense is not a quantity of anything, and
   * a total over some of the matches is not a smaller total but a wrong one.
   */
  total: string | null;
  sides: Record<string, string>;
  truncated: boolean;
  scope: ProfitLoss["scope"];
  basis: "lines_pulled";
}

export interface LedgerTransactionType {
  /** The raw QuickBooks type — the filter value sent back to the server. */
  name: string;
  /** The display label for the same type ("Credit Card Charge"), #1. */
  label: string;
  count: number;
}

export interface LedgerTransactionPage {
  /** MATCHING LINES — the denominator for the table's "1–100 of N". */
  total: number;
  /** How many QuickBooks documents those lines belong to. Never `total`. */
  documents: number;
  /**
   * ⚠️ Sum of the positive and of the negative line amounts, both positive.
   * Over a complete set of documents they are EQUAL and the net is zero: that
   * is double entry, not a bug. They mean something once the search is
   * narrowed — to one account they are that account's activity — so the screen
   * has to say which it is showing.
   */
  debit_total: string;
  credit_total: string;
  /**
   * B3: the account's net over the period, presented the same way the P&L and
   * trial balance present it — income positive, everything else debit-positive.
   * Only set when the read is narrowed to ONE account in ONE file (the
   * drill-down); null for a multi-account search, where a single net is
   * meaningless. This is the figure that ties to the P&L line and the TB line.
   */
  period_net: string | null;
  /**
   * Item C: the account's balance the day before `since`, in the stored
   * (debit-positive) sign — `present()` flips it for display like each row's
   * running balance. The screen pins a "Beginning balance" row carrying it, on
   * the first page only. Null when there is no period start or the view is not
   * the single-account running-balance order.
   */
  opening_balance: string | null;
  /** "income" | "expense" | null (a balance-sheet or unknown account). */
  account_side: "income" | "expense" | null;
  /** The drilled account's QuickBooks type / full path, when one is in scope. */
  account_type: string | null;
  account_full_name: string | null;
  /** The span the MATCHING ROWS cover, not the span that was filtered for. */
  earliest: string | null;
  latest: string | null;
  limit: number;
  offset: number;
  transactions: LedgerTransaction[];
  /** The types present in reach, so the filter offers what exists. */
  types: LedgerTransactionType[];
  files: BookFile[];
}

export interface BookTransactionFilters {
  file_id?: string;
  /** Only meaningful with `file_id`; the server answers 400 without one. */
  account_qbd_id?: string;
  transaction_type?: string;
  /** The precise filter in the Filters panel, on the entity name alone. */
  counterparty?: string;
  text?: string;
  since?: string;
  until?: string;
  min_amount?: string;
  max_amount?: string;
  /**
   * The one box at the top: every column at once — name, memo, reference,
   * type, account, file, date and amount. Amounts may be typed the way people
   * write them; `13,956.88` and `13956.88` are the same search.
   */
  search?: string;
  /**
   * The Sync tab's "show me these rows" link (`048`).
   *
   * ⚠️ This narrows to what a run LAST wrote, not what it wrote at the time:
   * `sync_run_id` moves with `pulled_at` because a row has one current
   * provenance. Any screen that sends this MUST say so on its face, or an
   * older run's shrinking row count reads as data going missing.
   */
  sync_run_id?: number;
  /**
   * ⚠️ SORTING IS THE SERVER'S JOB HERE, because this endpoint returns a page
   * out of a total. One of: date, type, counterparty, account, amount,
   * reference, memo, file.
   */
  sort?: string;
  dir?: "asc" | "desc";
}

/**
 * What happened to one trial-balance line between two pulls of the same date.
 *
 * `unmatched` is not a fifth kind of change -- it means the line carries no
 * account id, so no comparison was possible. `full_name`/`account_label` is not
 * a key and matching on it would pair a contribution with a distribution.
 */
export type LedgerLineChange =
  | "first"
  | "added"
  | "removed"
  | "restated"
  | "same"
  | "unmatched";

export interface TrialBalanceLine {
  account_qbd_id: string | null;
  account_label: string;
  /** A6: the QuickBooks account type and number, so the screen can order by
   *  chart-of-accounts type and group the rows. Null on a line with no account
   *  id (an unmatched/removed row, or a number-based file's report). */
  account_type?: string | null;
  account_number?: string | null;
  debit: string | null;
  credit: string | null;
  // Only on a QuickBooks pull being compared against an earlier pull of the
  // same date. A computed trial balance has no earlier pull to differ from.
  previous_debit?: string | null;
  previous_credit?: string | null;
  change?: LedgerLineChange;
}

export interface Restatement {
  added: number;
  removed: number;
  restated: number;
  /** Lines that could not be compared at all, because they carry no id. */
  unmatched: number;
}

export interface TrialBalancePull {
  id: string;
  /** Part of the identity, not metadata. One row per PULL, not per date. */
  pulled_at: string;
  source_request_id: string | null;
  total_debit: string | null;
  total_credit: string | null;
  line_count: number;
  /** Sides agree. A trial balance that does not is a broken pull. */
  balanced: boolean;
  previous_pulled_at: string | null;
  /** Null on the first pull of a date. Not the same as "nothing changed". */
  restatement: Restatement | null;
}

export interface TrialBalanceDate {
  as_of_date: string;
  pull_count: number;
  latest: string;
}

export interface TrialBalance {
  file_id: string | null;
  as_of: string | null;
  /**
   * "quickbooks" — QuickBooks' own trial balance, pulled on this exact date.
   * "computed" — rebuilt from the stored transaction lines (the same rebuild
   * the nightly tie-out checks against QuickBooks), for a date with no pull.
   */
  source: "quickbooks" | "computed";
  lines: TrialBalanceLine[];
  /** The whole report's totals, from the pull or the rebuild. */
  total_debit: string | null;
  total_credit: string | null;
  balanced: boolean;
  /** Dates QuickBooks' own trial balance was pulled — the authoritative ones. */
  pulled_dates: string[];
  /** When computed could not run (no anchor / fiscal year yet), why. */
  computed_reason: string | null;
  dates: TrialBalanceDate[];
  pulls: TrialBalancePull[];
  selected_pull: string | null;
  /** More pulls of this date exist than were returned. */
  truncated: boolean;
  files: BookFile[];
  /** B2: the latest tie-out verdict for this file — whether the rebuilt books
   *  tie to QuickBooks' own trial balance. Null when no file is resolved. */
  tieout?: LedgerTieout | null;
}

/**
 * One row of a balance-sheet statement, kept in QuickBooks' own order.
 *
 * `kind` is how the row is drawn: `text` a section header (no amount), `data`
 * an account line, `subtotal` a "Total …", `total` a grand total. `depth` is
 * the indentation, computed from the section nesting. `account_path` is the
 * full "Parent:Sub" path on a data row (there is no account id on this report),
 * null elsewhere.
 */
export interface BalanceSheetLine {
  row_number: number;
  kind: "text" | "data" | "subtotal" | "total";
  depth: number;
  label: string;
  account_path: string | null;
  amount: string | null;
  /** The account id this line's path resolved to, so a `data` row can drill to
   *  the account detail (Chaim: Balance-Sheet lines must be clickable). Null
   *  when the path matched no account or more than one, or on a non-data row. */
  account_qbd_id?: string | null;
}

export interface BalanceSheetPull {
  id: string;
  pulled_at: string;
  source_request_id: string | null;
  total_assets: string | null;
  total_liabilities_equity: string | null;
  /** Assets equal liabilities + equity. A pull where they do not is broken. */
  balanced: boolean;
  row_count: number;
}

/**
 * Round-3 #6: the balance sheet as COLUMNS — one column per month-end snapshot,
 * or a two-date comparison with $ and % change. A column is a period (a snapshot
 * date, carried in `as_of`) or a derived delta. Each QuickBooks row carries a
 * value per column key (money decimal string, or a percent number string / ""
 * for % Change). A column exists only where a snapshot does.
 */
export interface BSColumnDef {
  key: string;
  label: string;
  kind: "period" | "delta_abs" | "delta_pct";
  as_of?: string;
}

export interface BSColumnRow {
  kind: "text" | "data" | "subtotal" | "total";
  depth: number;
  label: string;
  account_path: string | null;
  qbd_id: string | null;
  values: Record<string, string>;
}

export interface BSColumns {
  file_id: string;
  defs: BSColumnDef[];
  rows: BSColumnRow[];
}

export interface BalanceSheet {
  file_id: string | null;
  as_of: string | null;
  dates: TrialBalanceDate[];
  pulls: BalanceSheetPull[];
  selected_pull: string | null;
  lines: BalanceSheetLine[];
  /** Round-3 #6: the columnar view, or null in the default single-date sheet. */
  columns: BSColumns | null;
  /**
   * Round-3 #4: the chosen date has no saved snapshot (the screen offers to
   * pull it rather than silently showing the latest), and whether the file is
   * connected to QuickBooks so a pull is even possible.
   */
  needs_pull: boolean;
  connected: boolean;
  /** More pulls of this date exist than were returned. */
  truncated: boolean;
  files: BookFile[];
  /** B2: the latest tie-out verdict for this file (see TrialBalance.tieout). */
  tieout?: LedgerTieout | null;
}

/**
 * ⚠️ `unchecked` is not a lesser `checked`. It means the count check DID NOT
 * RUN -- `count_check_passed` is NULL, and the high-water mark is not advanced
 * by such a run. Drawing it as a tick invents a guarantee.
 */
export type LedgerRunState =
  | "in_flight"
  | "failed"
  | "check_failed"
  | "unchecked"
  | "checked";

/**
 * One request to read the company files, run by the loop in the API process.
 * The envelope around a sweep's `LedgerSyncRun` rows: counts and timestamps,
 * so "14 files, 2 failed, finished 14:26" does not need 28 rows read.
 */
/**
 * Why a sweep ended before its file list did. All three are connection
 * problems -- Conductor could not get the request through to the QuickBooks PC
 * -- and the backend's `_STOP_REASONS` (api/qbd_sync.py) is the source of the
 * values. The frontend knew only `connection_not_active` for a while after
 * `070`/`045` split out the two specific codes, so a `qbd_connection_error`
 * stop rendered as if the sweep had run clean (the live symptom on 2026-10-29);
 * this union is the fix, and it must stay in step with `_STOP_REASONS`.
 */
export type SweepStopReason =
  | "connection_not_active"
  | "qbd_connection_error"
  | "qbd_request_timeout";

export interface LedgerSweep {
  id: number;
  /** Who decided: a person on this tab, the timetable, or the loop re-queueing a sweep a restart cut short. */
  trigger: "manual" | "schedule" | "resume";
  requested_at: string;
  /** Null means queued, waiting for the loop. */
  started_at: string | null;
  /** Null means queued or running. */
  finished_at: string | null;
  files_planned: number;
  files_done: number;
  files_failed: number;
  /** Planned, never attempted because the sweep stopped. NOT a failure. */
  files_skipped: number;
  /**
   * Why it ended before its list did, or null if it ran to the end. See
   * `SweepStopReason`. Any of them covers TWO opposite states -- the Web
   * Connector mid-session on something else (busy, come back later) or not
   * running at all (stopped, somebody must go to the Windows box) -- and the
   * code does NOT say which. The line's `error_message` carries Conductor's
   * "last active ..." clause, which does. Never render one of the two as fact.
   */
  stopped_reason: SweepStopReason | null;
  /** The file being read right now. Null for everyone but the builder. */
  current_file: string | null;
  error: string | null;
  resumes: number | null;
  requested_by_name: string;
  /** Set when the sweep was for one file. Null for everyone but the builder. */
  file_name?: string | null;
}

/** One line per file in a finished pull. Derived from the run rows (044). */
export interface LedgerSweepReportLine {
  file_id: string;
  file_name: string;
  /** Every feed this file attempted came back clean. */
  ok: boolean;
  rows_written: number;
  /** Null where no feed ran its count check: absent, not failed. */
  checked: boolean | null;
  unchecked_feeds: number;
  started_at: string | null;
  finished_at: string | null;
  seconds: number | null;
  error_code: string | null;
  error_message: string | null;
  /** The only id Conductor's support can act on. */
  error_request_id: string | null;
}

/**
 * What the last finished pull did -- Jonathan, 2026-09-22: *"i want a report
 * after a pull. not a whole list of red rows."*
 *
 * The counts come from the sweep row and describe what HAPPENED; `files` is
 * what THIS reader is allowed to see, so `files.length` can be smaller than
 * `planned` and the two must never be presented as the same number.
 */
export interface LedgerSweepReport {
  sweep_id: number;
  files: LedgerSweepReportLine[];
  planned: number;
  done: number;
  failed: number;
  skipped: number;
  stopped_reason: SweepStopReason | null;
  finished_at: string | null;
  rows_written: number;
}

/** One account that did not tie, rebuilt vs. QuickBooks' trial balance. */
export interface LedgerTieoutDiff {
  account_qbd_id: string | null;
  account_label: string | null;
  // QuickBooks money is a decimal STRING (see the note above `amount`).
  rebuilt: string | null;
  reported: string | null;
  difference: string | null;
  /** Which checked date this mismatch came from (file-level verdict only). */
  as_of_date?: string;
}

/** One checked date within a file-level tie-out run (Chaim 2026-10-21). */
export interface LedgerTieoutDate {
  as_of_date: string;
  trusted: boolean | null;
  reason: string | null;
  anchor_date: string | null;
  accounts_checked: number;
  accounts_mismatched: number;
}

/**
 * The latest tie-out verdict for a file (Change 5): whether the books rebuilt
 * from stored lines match QuickBooks' own trial balance, account by account.
 * `trusted` is three-valued: null = could not run (see `reason`), true = every
 * account tied to the cent, false = `diffs` lists the accounts that did not.
 *
 * Since Chaim's 2026-10-21 answer this is a FILE-LEVEL verdict: the books are
 * tied out at several dates (the last two month-ends and the last fiscal
 * year-end), and `trusted` is the AND over them — true only when every date
 * tied. `dates` carries the per-date breakdown; each row of `diffs` is tagged
 * with the `as_of_date` it came from. (Older files with only a single pre-batch
 * run come back with `dates` absent, and the badge still reads correctly.)
 */
export interface LedgerTieout {
  id: string;
  batch_id?: string;
  as_of_date: string;
  computed_at: string;
  trusted: boolean | null;
  reason: string | null;
  anchor_date: string | null;
  accounts_checked: number;
  accounts_mismatched: number;
  dates?: LedgerTieoutDate[];
  diffs: LedgerTieoutDiff[];
  /**
   * Round-3 #7: whether this verdict is riding stale data. `last_sync_at` is
   * when QuickBooks was last reached successfully (the "since …" in the
   * warning); `last_sync_failed` is whether the file's most recent sync run
   * errored (e.g. disconnected). The badge shows the check time regardless and
   * warns when the data is stale — a "Ties" verdict on a disconnected file must
   * not read as reassurance.
   */
  last_sync_at?: string | null;
  last_sync_failed?: boolean;
}

export interface LedgerSweepState {
  open: LedgerSweep | null;
  recent: LedgerSweep[];
  /** The last FINISHED pull, as a report. Null before anything has run. */
  report: LedgerSweepReport | null;
  schedule: {
    enabled: boolean;
    time: string;
    tz: string;
    due: string | null;
    problem: string | null;
  };
  /** Builder, and a Conductor key on this deployment. Same as `can_run_all`. */
  can_run: boolean;
  /** Every file, ~45 minutes on Vineyard's server: the builder only. */
  can_run_all: boolean;
  /** One file, ~3 minutes: anyone who runs the book, on a file they can see. */
  can_run_one: boolean;
}

export interface LedgerSyncRun {
  id: number;
  feed: "accounts" | "transactions" | "trial_balance";
  started_at: string;
  /** Null means IN FLIGHT. QuickBooks serves one request at a time per host. */
  finished_at: string | null;
  pages: number;
  rows_seen: number;
  /**
   * Upserts. ⚠️ NOT arrivals, and the Sync tab does not lead with it: the
   * transactions feed is incremental, so most of this is rows already here
   * being rewritten because QuickBooks bumped `updatedAt` on them. `048`.
   */
  rows_written: number;
  /** Rows this run had never seen. The number somebody came here for. */
  rows_new: number;
  /** Rows that already existed and were rewritten. Usually most of them. */
  rows_updated: number;
  count_check: string | null;
  count_check_passed: boolean | null;
  high_water_updated_at: string | null;
  error_code: string | null;
  error_message: string | null;
  /** The only identifier Conductor's support can act on. */
  error_request_id: string | null;
  seconds: number;
  file_id: string;
  file_name: string;
  requested_by_name: string | null;
  state: LedgerRunState;
  /** The nightly sync, with no person attached. Not a missing name. */
  unattended: boolean;
}

/**
 * What one run brought in, and where it went.
 *
 * Freund, 2026-09-23: *"it says the transactions, but I don't know where the
 * transactions are."* `LedgerSyncRun` says whether a run can be trusted; this
 * says what was in it.
 */
export interface LedgerRunContents {
  run: LedgerSyncRun & { rows_new: number; rows_updated: number };
  /**
   * ⚠️ `null` means THIS FEED HAS NO ROW LINK, which is a different sentence
   * from "this run brought nothing" and renders differently. The chart and
   * the trial balance are already reachable by other routes (`048`), so only
   * the transactions feed carries this.
   */
  rows: {
    /**
     * Rows still carrying this run's id. ⚠️ NOT `rows_written`: `sync_run_id`
     * names the run that LAST wrote a row, so a later run re-touching a row
     * takes it. This number shrinks over time and that is correct.
     */
    still_attributed: number;
    /** Transactions, not lines. A bill with four lines is one document. */
    documents: number;
    earliest: string | null;
    latest: string | null;
    /**
     * The rows later runs have claimed. Shown in words, because a count that
     * shrinks with no explanation reads as data going missing -- the one
     * thing a books screen must never look like.
     */
    claimed_by_later: number;
    /**
     * ⚠️ Counts only. There is deliberately no money figure: a row is a LINE,
     * so the amounts are the two sides of double entry. `access.py` says why
     * the absolute sum was tried and dropped.
     */
    by_type: { label: string; lines: number }[];
    by_account: { qbd_id: string | null; label: string | null; lines: number }[];
    accounts_shown: number;
    accounts_total: number;
  } | null;
}


// ---------------------------------------------------------------------------
// Payables. Four screens, one shape of money: integer cents, never divided.
// ---------------------------------------------------------------------------
// --- Feedback ---------------------------------------------------------------
export type FeedbackKind = "bug" | "change" | "question" | "other";
export type FeedbackStatus = "open" | "in_review" | "resolved" | "wont_fix";

/**
 * One thing somebody wants changed about a screen. Seen by whoever filed it
 * and by the builder -- a screenshot can hold the whole book, so the list is
 * not open to every login the way the PM Tool original is.
 */
export interface FeedbackItem {
  id: string;
  title: string;
  body: string | null;
  kind: FeedbackKind;
  status: FeedbackStatus;
  /** The path they were on when they filed it. */
  page: string | null;
  has_screenshot: boolean;
  filed_by: string;
  filed_by_name: string;
  filed_at: string;
  status_set_at: string | null;
  comments: number;
}

export interface FeedbackComment {
  id: string;
  body: string;
  written_by: string;
  written_by_name: string;
  written_at: string;
}

export interface Vendor {
  id: string;
  name: string;
  legal_name: string | null;
  status: "active" | "on_hold" | "archived";
  w9_status: "none" | "requested" | "on_file" | "not_required";
  w9_received_on: string | null;
  tax_id_last4: string | null;
  created_at: string;
  /** Reach-filtered. Two people see two different numbers here, correctly. */
  open_invoices: number;
  open_cents: number;
  last_invoice_at: string | null;
  bank_institution: string | null;
  bank_last4: string | null;
  bank_approved_at: string | null;
  pending_changes: number;
  /** The companies that have paid this vendor, reach-filtered. A vendor is
   *  group-wide, so this is who uses it, not an owner — empty when the reader
   *  has paid it from nowhere they can see. */
  companies: string[];
}

export type Rail = "ach" | "wire" | "zelle" | "check" | "card";

export interface VendorBankDetail {
  id: string;
  state: "proposed" | "active" | "rejected" | "superseded";
  rail: Rail;
  institution_name: string | null;
  routing_number: string | null;
  wire_routing_number: string | null;
  swift_code: string | null;
  account_last4: string | null;
  account_kind: "checking" | "savings";
  handle: string | null;
  payee_name: string | null;
  source: string;
  note: string | null;
  announced_change: boolean;
  change_notice_text: string | null;
  proposed_at: string;
  approved_at: string | null;
  proposed_by_name: string;
  approved_by_name: string | null;
}

export type InvoiceStatus =
  | "received"
  | "in_review"
  | "awaiting"
  | "approved"
  | "declined"
  | "scheduled"
  | "paid"
  | "void";

export interface Invoice {
  id: string;
  invoice_number: string | null;
  invoice_date: string | null;
  due_date: string | null;
  amount_cents: number;
  status: InvoiceStatus;
  source: string;
  /** NULL means a person typed it. That is unmeasured, not certain. */
  extraction_confidence: number | null;
  received_at: string;
  duplicate_of: string | null;
  entity_id: string;
  entity_name: string;
  vendor_id: string;
  vendor_name: string;
  overdue: boolean;
  approval_id: string | null;
  approval_state: string | null;
  /**
   * When this bill was recorded paid — the release date of the payment that
   * settled it. NULL until then. "Recorded paid", not "cleared": a person logs
   * a release through their own bank, which is the only paid moment this system
   * sees (there is no bank-clearing feed).
   */
  paid_at: string | null;
  /**
   * The installment schedule (050), if the bill is paid in parts — one entry per
   * payment, on its own date. Empty for an ordinary bill. `overdue` already
   * accounts for a past installment, since a scheduled bill carries no own due
   * date.
   */
  installment_count: number;
  installments: { seq: number; amount_cents: number; due_date: string }[];
  /**
   * Vendor credit netted against this bill (051), in cents. 0 for a bill with
   * no credit applied. ⚠️ A TRACKED FACT, not a reduction of `amount_cents`,
   * which is always the bill's full face figure (021) — and the payment path
   * does NOT yet net this automatically. The card says so; nobody should read
   * it as "the payment was reduced".
   */
  credit_applied_cents: number;
}

export interface InvoiceTotals {
  invoices: number;
  amount_cents: number;
  overdue: number;
  overdue_cents: number;
  next_due: string | null;
}

/**
 * What `POST /api/invoices/extract` read off an uploaded invoice. Nothing is
 * stored by that call — this fills the Enter-a-bill form, and a person commits
 * it through `createInvoice`. Every field may be null; `is_credit` warns the
 * total was negative (a credit note, which is not a payable yet).
 */
export interface InvoiceExtraction {
  read: {
    vendor_name: string | null;
    amount_cents: number | null;
    amount_raw: string | null;
    is_credit: boolean;
    invoice_date: string | null;
    due_date: string | null;
    invoice_number: string | null;
    installments: { amount_cents: number | null; due_date: string | null }[];
  };
  /** The vendor the read name resolved to, or null for one not on the list. */
  vendor: { id: string; name: string; match: "exact" | "fuzzy" } | null;
}

/**
 * One item in the AP-inbox review queue (081). What ingestion read off an ap@
 * message; every extracted field may be null. A person confirms the vendor,
 * assigns the company and commits it to an invoice, or dismisses a notice.
 */
export type ApInboxItem = {
  id: string;
  message_id: string;
  attachment_id: string | null;
  received_at: string | null;
  from_address: string | null;
  subject: string | null;
  attachment_name: string | null;
  content_type: string | null;
  extracted_vendor_name: string | null;
  amount_cents: number | null;
  is_credit: boolean;
  invoice_date: string | null;
  due_date: string | null;
  invoice_number: string | null;
  installments: { amount_cents: number | null; due_date: string | null }[];
  extraction_error: string | null;
  state: "needs_review" | "notification" | "committed" | "dismissed";
  ingested_at: string;
  matched_vendor_id: string | null;
  matched_vendor_name: string | null;
  has_document: boolean;
  /** Mailbox folder the row came from; `"trello"` marks the one-time cutover batch. */
  source_folder: string;
  /** The Trello card to open for a migration row (it has no stored document); null for mail. */
  external_url: string | null;
  committed_invoice_id: string | null;
  dismiss_reason: string | null;
  /** Why the ingest classifier routed this to the notifications bucket (088) —
   * the signal that fired (statement, past due, overdue, reminder, …). Null on
   * the ordinary invoice path. */
  notification_reason: string | null;
};

export type ApInboxCounts = {
  needs_review: number;
  notification: number;
  committed: number;
  dismissed: number;
  /** Open invoices still in the one-time Trello cutover section (P93); drives its tab. */
  trello?: number;
};

/**
 * A standalone vendor credit (051). Its own document, owed to us — never a
 * negative invoice. `remaining_cents` is the face amount less what has been
 * applied; a credit is useful up to that figure, and it is what the intake
 * prompt and the apply control are measured against. Applying one is a human
 * act that moves no money: it records the netting and stops the same credit
 * being spent twice.
 */
export interface CreditNote {
  id: string;
  credit_number: string | null;
  issued_date: string | null;
  amount_cents: number;
  status: "open" | "part_applied" | "applied" | "void";
  source: string;
  extraction_confidence: number | null;
  received_at: string;
  entity_id: string;
  entity_name: string;
  vendor_id: string;
  vendor_name: string;
  created_by_name: string;
  applied_cents: number;
  remaining_cents: number;
}

/** A standing instruction that makes one bill a month, for a vendor who sends
 * none (058). It is not a bill itself and moves no money. */
export interface RecurringBill {
  id: string;
  entity_id: string;
  entity_name: string;
  vendor_id: string;
  vendor_name: string;
  amount_cents: number;
  day_of_month: number;
  memo: string | null;
  status: "active" | "paused" | "ended";
  starts_on: string;
  ends_on: string | null;
  created_at: string;
  /** How many bills it should make before it stops and waits for a decision
   *  (103), or null to run until paused or ended. */
  occurrence_limit: number | null;
  /** How many it has made so far, lifetime. "made X of N" is this and the
   *  limit; "reached its limit" is occurrences_made >= occurrence_limit. */
  occurrences_made: number;
  /** The first-of-month of the last bill this template produced, or null. */
  last_period: string | null;
  last_invoice_id: string | null;
}

/** An expected cash movement a person maintains by hand (059) — an investment
 * committed, a loan given, a loan coming back. Moves no money; feeds the
 * available-cash forecast for its date. */
/** Part of a loan paid (075). */
export interface ObligationPayment {
  id: string;
  amount_cents: number;
  paid_on: string;
  note: string | null;
}

export interface Obligation {
  id: string;
  entity_id: string;
  entity_name: string;
  direction: "outflow" | "inflow";
  kind:
    | "investment"
    | "loan_given"
    | "loan_return"
    | "income"
    | "investment_return"
    | "other";
  amount_cents: number;
  due_date: string;
  counterparty: string | null;
  note: string | null;
  status: "expected" | "settled" | "cancelled";
  created_at: string;
  related_obligation_id: string | null;
  /** The commitment this is a tranche of (066); null for a standalone one. */
  commitment_id: string | null;
  /** A loan given folds its expected return in: amount, date and its status. */
  return_id: string | null;
  return_amount_cents: number | null;
  return_due_date: string | null;
  return_status: "expected" | "settled" | "cancelled" | null;
  /** The account this commitment is tied to (067), for bookkeeping. */
  account_id: string | null;
  account_name: string | null;
  /** The investment-only fields (086); null on every non-investment kind. The
   *  investing entity is entity_id/entity_name above, reused not duplicated. */
  investment_name: string | null;
  investment_category_id: string | null;
  investment_category_name: string | null;
  investment_contact_name: string | null;
  investment_contact_phone: string | null;
  investment_contact_email: string | null;
  /** How many documents hang off this commitment (065). */
  document_count: number;
  /** Partial payments on a loan (075). Outstanding = amount − paid_cents. */
  paid_cents: number;
  payments: ObligationPayment[];
  /** The same for a loan given's folded-in return leg. */
  return_paid_cents: number;
  return_payments: ObligationPayment[];
}

/** An add-as-you-go investment category (086) for the commitment Category
 *  picklist. Global reference data, case-insensitively unique. */
export interface InvestmentCategory {
  id: string;
  name: string;
}

/** A document attached to an obligation (065) — metadata; bytes are fetched
 *  one at a time from the download URL. */
export interface ObligationDocument {
  id: string;
  filename: string;
  media_type: string;
  byte_size: number;
  uploaded_at: string;
  uploaded_by_name: string;
}

/** The actual file behind a bill (102). Same shape as a stored obligation
 *  document; kept a distinct type so the two domains stay uncoupled. */
export interface InvoiceDocument {
  id: string;
  filename: string;
  media_type: string;
  byte_size: number;
  uploaded_at: string;
  uploaded_by_name: string;
}

/** A bill already on the system that looks like the one being entered. */
export interface PossibleDuplicate {
  id: string;
  invoice_number: string | null;
  invoice_date: string | null;
  due_date: string | null;
  amount_cents: number;
  status: InvoiceStatus;
  received_at: string;
}

export interface Approval {
  id: string;
  subject_type: "invoice" | "vendor_bank_detail" | "payment";
  subject_id: string;
  state: "pending" | "approved" | "declined" | "withdrawn";
  amount_cents: number | null;
  requested_at: string;
  decided_at: string | null;
  note: string | null;
  requested_by: string;
  requested_by_name: string;
  decided_by_name: string | null;
  entity_name: string | null;
  vendor_name: string | null;
  invoice_number: string | null;
  due_date: string | null;
  rail: Rail | null;
  institution_name: string | null;
  routing_number: string | null;
  wire_routing_number: string | null;
  swift_code: string | null;
  account_last4: string | null;
  handle: string | null;
  payee_name: string | null;
  bank_source: string | null;
  /** The vendor's own claim that their details changed. Surfaced, blocks nothing. */
  announced_change: boolean;
  change_notice_text: string | null;
  previous_last4: string | null;
  previous_institution: string | null;
  previous_handle: string | null;
  previous_payee_name: string | null;
  /** Answered by the server, so a button is never offered that will be refused. */
  can_decide: boolean;
  /**
   * Who it is waiting on: the people who could decide this, by the same
   * four-eyes rule as `can_decide` — an approve grant on the entity (or any
   * approve grant for an entity-less bank-detail change), never the requester.
   * Empty means nobody but the requester can approve it (a routing gap).
   */
  eligible_approvers: string[];
}

export interface Payment {
  id: string;
  amount_cents: number;
  method: "wire" | "ach" | "check" | "quickpay" | "other";
  state: "prepared" | "released" | "void";
  scheduled_for: string | null;
  prepared_at: string;
  released_at: string | null;
  bank_reference: string | null;
  note: string | null;
  entity_name: string;
  vendor_name: string;
  from_account: string | null;
  from_last4: string | null;
  to_institution: string | null;
  to_last4: string | null;
  prepared_by_name: string;
  released_by_name: string | null;
  approval_state: string | null;
  approval_id: string | null;
  invoices: number;
  /**
   * The auto-approval engine's latest reading (054), or null if it has not
   * looked at this payment. Shown so Sarah can watch what the policy WOULD do
   * during the shadow month. `auto_cleared` is the armed, counted case; it
   * stays false while the policy is only being watched.
   */
  auto_verdict: "would_clear" | "needs_review" | "ineligible" | null;
  auto_reason: string | null;
  auto_cleared: boolean | null;
  /** Check printing (101). The account this is drawn from, whether that account
   * is set up to print checks, and the live check number if one is printed. */
  from_account_id: string | null;
  from_account_prints_checks: boolean;
  check_number: number | null;
}

/** An account's check-printing setup (101). The full account number is never
 * sent to the browser — only its last four. */
export interface CheckConfig {
  account_id: string;
  routing_number: string;
  account_number_last4: string;
  drawer_name: string;
  drawer_address: string | null;
  bank_name: string;
  next_check_number: number;
  enabled: boolean;
  account_name: string;
  entity_id: string;
  highest_issued: number | null;
}

/** One row on a snapshot: an account, labelled by its entity, with its three
 * as-of balances and the plain line that explains the move. Cents, like
 * everything here; `null` is a balance with no reading, which is not zero. */
export interface SnapshotAccount {
  entity_name: string;
  account_name: string;
  today_cents: number | null;
  prior_cents: number | null;
  week_cents: number | null;
  delta_cents: number | null;
  note: string;
}

export interface SnapshotInstitution {
  name: string;
  /** `active` when a feed row landed today, `quiet` when the balance only drifted. */
  status: "active" | "quiet";
  subtotal_today_cents: number;
  accounts: SnapshotAccount[];
}

export interface Snapshot {
  title: string;
  as_of: string;
  columns: { today: string; prior: string; week: string };
  institutions: SnapshotInstitution[];
  signed_total_today_cents: number | null;
  signed_total_prior_cents: number | null;
  change_vs_prior_cents: number | null;
  change_vs_week_cents: number | null;
  freshness: { as_of: string | null; age_seconds: number | null };
  scope: { label: string; members_hidden: number };
}

/** One turn in the assistant conversation. The client sends the whole list up
 * each time; `assistant` turns are the model's prior replies. */
export interface AssistantMessage {
  role: "user" | "assistant";
  content: string;
}

/**
 * The Rules / approval screen (Chaim §4, §6.2).
 *
 * `RuleResolution` is the one thing every rule row carries that a person has to
 * see: not just the account it names, but whether that name still resolves to
 * exactly one active account in the live chart. An unsigned OR unresolvable
 * rule routes its lines to a person, so the screen shows both facts.
 */
export type RuleType = "vendor" | "intercompany" | "payroll";

export interface RuleResolution {
  full_name: string | null;
  resolves: boolean;
  resolved_qbd_id: string | null;
  resolved_name: string | null;
}

export interface VendorRule {
  id: string;
  match_text: string;
  signed_off: boolean;
  /** Who blessed this rule and when, or null while unsigned. */
  signed_by: string | null;
  signed_at: string | null;
  source: string | null;
  /** How many bank lines in the file this rule's text matches — its reach. */
  match_count: number;
  account: RuleResolution;
}

export interface IntercompanyRule {
  id: string;
  last4: string;
  counterparty_name: string;
  out_treatment: string | null;
  in_treatment: string | null;
  note: string | null;
  signed_off: boolean;
  signed_by: string | null;
  signed_at: string | null;
  source: string | null;
  out_account: RuleResolution;
  in_account: RuleResolution;
}

export interface PayrollRule {
  id: string;
  employee_name: string;
  signed_off: boolean;
  signed_by: string | null;
  signed_at: string | null;
  source: string | null;
  account: RuleResolution;
}

export interface RuleCandidateAccount {
  full_name: string | null;
  resolves: boolean;
  qbd_id: string;
}

export interface RuleCandidateAlternative {
  qbd_id: string;
  full_name: string | null;
  confidence: number;
  because: string;
}

/** A payee the engine learned from history but which is not yet a signed rule.
 *  Approving it inserts a signed recurring-vendor rule. */
export interface LearnedCandidate {
  payee_key: string;
  display_name: string;
  suggested_match_text: string;
  count: number;
  confidence: number;
  /** One account in the history (ready), or several (flagged — §4.5). */
  consistent: boolean;
  meets_cutoff: boolean;
  suggested_account: RuleCandidateAccount;
  alternatives: RuleCandidateAlternative[];
  amount_range: { min_cents: number; max_cents: number; median_cents: number };
  examples: Array<{
    posted_on: string;
    name: string | null;
    counterparty_name: string | null;
    amount_cents: number;
  }>;
}

export interface ClassificationAutoApprove {
  /** 'off', or a confidence like '0.97'. */
  value: string;
  is_on: boolean;
  updated_by_name: string | null;
}

export interface ClassificationRules {
  file_id: string;
  vendor_rules: VendorRule[];
  intercompany_rules: IntercompanyRule[];
  payroll_rules: PayrollRule[];
  candidates: LearnedCandidate[];
  auto_approve: ClassificationAutoApprove;
}

export interface ClassificationAutoApproveResult {
  setting: ClassificationAutoApprove;
  run: { auto_approved: number; totals: Record<string, number> } | null;
}

export const api = {
  login: (email: string, password: string) =>
    post<User>("/api/auth/login", { email, password }),
  logout: () => post<void>("/api/auth/logout"),
  me: () => get<User>("/api/auth/me"),
  changePassword: (current_password: string, new_password: string) =>
    post<void>("/api/auth/password", { current_password, new_password }),

  dashboard: () => get<Dashboard>("/api/dashboard"),

  // -------------------------------------------------------------------------
  // The books. Every one of these reads the database; none of them reaches
  // QuickBooks. A cold Conductor call opens a company file on Vineyard's
  // Windows server and takes ~30 seconds, and QuickBooks serves one request at
  // a time per machine, so nothing user-facing may wait on it.
  // -------------------------------------------------------------------------
  bookFiles: () =>
    get<{ count: number; files: BookFile[]; any_connected: boolean }>(
      "/api/books/files",
    ),

  /**
   * The chart of accounts, inactive ones included.
   *
   * `since` / `until` narrow the per-account LINE COUNTS only. Every account
   * comes back either way, because the chart is the chart -- and no window
   * touches `balance`, which is a running total as at the last sync.
   */
  bookAccounts: (fileId?: string, window: { since?: string; until?: string } = {}) => {
    const query = new URLSearchParams();
    if (fileId) query.set("file_id", fileId);
    if (window.since) query.set("since", window.since);
    if (window.until) query.set("until", window.until);
    const suffix = query.toString();
    return get<{
      count: number;
      accounts: LedgerAccount[];
      inactive: number;
      files: BookFile[];
    }>(`/api/books/accounts${suffix ? `?${suffix}` : ""}`);
  },

  /**
   * What the books say moved. One row per LINE — read `total` and `documents`
   * as the two different counts they are.
   *
   * ⚠️ `account_qbd_id` without `file_id` is a 400 from the server, on
   * purpose: a QuickBooks account id is unique inside one company file and
   * nothing stops two files reusing the string, so the unpaired filter would
   * return two files' rows under one account's name.
   */
  bookTransactions: (
    filters: BookTransactionFilters & { limit?: number; offset?: number } = {},
  ) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== "") {
        query.set(key, String(value));
      }
    }
    const suffix = query.toString();
    return get<LedgerTransactionPage>(
      `/api/books/transactions${suffix ? `?${suffix}` : ""}`,
    );
  },

  /**
   * Income and expense over a RANGE — never an as-of. Trial balance and
   * balance sheet are as-of; P&L and general ledger take a range, and getting
   * it backwards produces numbers that look plausible and are wrong.
   */
  /**
   * One line of English -> an account and a period -> a figure (R4).
   *
   * ⚠️ No language model is involved on the server either — see `api/ask.py`.
   * The question is parsed by rule and everything it understood comes back in
   * `interpretation` to be shown.
   */
  bookAsk: (q: string, scope: { group?: string; category?: string } = {}) => {
    const query = new URLSearchParams({ q });
    if (scope.group) query.set("group", scope.group);
    else if (scope.category) query.set("category", scope.category);
    return get<AskAnswer>(`/api/books/ask?${query.toString()}`);
  },

  /** The institution-grouped balance snapshot over a scope. The same picture
   * the daily mail sends, drawn over the signed-in user's reach. */
  snapshot: (scope: { group?: string; category?: string } = {}) => {
    const query = new URLSearchParams();
    if (scope.group) query.set("group", scope.group);
    else if (scope.category) query.set("category", scope.category);
    const suffix = query.toString();
    return get<Snapshot>(`/api/snapshots/view${suffix ? `?${suffix}` : ""}`);
  },

  /** Whether the chat assistant is armed (a key, and ASSISTANT_ENABLED). The
   * bubble hides itself when this is false, so it is never a button that
   * only errors. */
  assistantStatus: () => get<{ enabled: boolean }>("/api/assistant/status"),

  /** One chat turn. The whole conversation goes up each time (the server holds
   * nothing); the reply comes back as plain text. */
  assistantChat: (messages: AssistantMessage[]) =>
    post<{ reply: string }>("/api/assistant/chat", { messages }),

  bookProfitLoss: (filters: {
    file_id?: string;
    since?: string;
    until?: string;
    /** A saved group's id, or a category key. The same scope every page takes. */
    group?: string;
    category?: string;
    /** Round-3 #5: "total" | "months" | "quarters" | "years". */
    columns?: string;
    /** Round-3 #5: "previous" | "year" | "custom" (with compare_since/until). */
    compare?: string;
    compare_since?: string;
    compare_until?: string;
  }) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value) query.set(key, value);
    }
    const suffix = query.toString();
    return get<ProfitLoss>(
      `/api/books/profit-and-loss${suffix ? `?${suffix}` : ""}`,
    );
  },

  /**
   * One as-of date, and every pull taken of it.
   *
   * `asOf` and `pull` are both optional and both resolved server-side: the
   * most recently pulled date, and the most recent pull of it. Asking for
   * today's date on a file nobody has pulled today would render an empty
   * screen over data that exists.
   */
  bookTrialBalance: (opts: {
    fileId?: string;
    asOf?: string;
    pull?: string;
  } = {}) => {
    const query = new URLSearchParams();
    if (opts.fileId) query.set("file_id", opts.fileId);
    if (opts.asOf) query.set("as_of", opts.asOf);
    if (opts.pull) query.set("pull", opts.pull);
    const suffix = query.toString();
    return get<TrialBalance>(
      `/api/books/trial-balance${suffix ? `?${suffix}` : ""}`,
    );
  },

  /**
   * A balance sheet AS OF a date, and every pull taken of it. The twin of
   * `bookTrialBalance`, keyed the same way for the same reason — open periods
   * get restated, so a date can have more than one pull.
   */
  bookBalanceSheet: (opts: {
    fileId?: string;
    asOf?: string;
    pull?: string;
    /** Round-3 #6: "total" | "months" (with since/until). */
    columns?: string;
    since?: string;
    until?: string;
    /** Round-3 #6: a second snapshot date to compare against. */
    compareAsOf?: string;
  } = {}) => {
    const query = new URLSearchParams();
    if (opts.fileId) query.set("file_id", opts.fileId);
    if (opts.asOf) query.set("as_of", opts.asOf);
    if (opts.pull) query.set("pull", opts.pull);
    if (opts.columns && opts.columns !== "total") query.set("columns", opts.columns);
    if (opts.since) query.set("since", opts.since);
    if (opts.until) query.set("until", opts.until);
    if (opts.compareAsOf) query.set("compare_as_of", opts.compareAsOf);
    const suffix = query.toString();
    return get<BalanceSheet>(
      `/api/books/balance-sheet${suffix ? `?${suffix}` : ""}`,
    );
  },

  /** Round-3 #4: queue an on-demand balance-sheet pull for a date that has no
   *  snapshot. 202 with the sweep to poll; 409 if a sync is already running. */
  requestBalanceSheetPull: (fileId: string, asOf: string) =>
    post<{ sweep: LedgerSweep; note: string }>(
      "/api/books/balance-sheet/pull",
      { file_id: fileId, as_of: asOf },
    ),

  /** How sure the agent has to be before it books rather than asks. */
  classificationSettings: () =>
    get<ClassificationSettings>("/api/settings/classification"),

  /** Builder only; 403 otherwise, 400 with a sentence for a bad value. */
  setClassificationCutoff: (value: string) =>
    put<ClassificationSettings>("/api/settings/classification/cutoff", {
      value,
    }),

  bookSync: (fileId?: string) =>
    get<{
      count: number;
      runs: LedgerSyncRun[];
      files: BookFile[];
      states: Record<LedgerRunState, number>;
      sweep: LedgerSweepState;
      /** Latest tie-out verdict per file id; null for a file never tied out. */
      tieouts: Record<string, LedgerTieout | null>;
    }>(
      fileId
        ? `/api/books/sync?file_id=${encodeURIComponent(fileId)}`
        : "/api/books/sync",
    ),

  /** What one run brought in. 404 for a run on a file out of reach. */
  bookRunContents: (runId: number) =>
    get<LedgerRunContents>(`/api/books/sync/runs/${runId}`),

  /**
   * Ask for the connected company files to be read. 202: queued, and the loop
   * in the API process starts it within a second. Builder-only; 409 while a
   * sweep is already queued or running.
   */
  requestSweep: (fileId?: string, fileIds?: string[]) =>
    post<{ sweep: LedgerSweep; note: string }>(
      "/api/books/sync",
      fileIds && fileIds.length
        ? { file_ids: fileIds }
        : fileId
          ? { file_id: fileId }
          : {},
    ),

  /** Cancel the open sweep — queued (dropped) or running (stops after its
   * current file). */
  cancelSweep: () =>
    post<{ cancelled: boolean; note: string }>("/api/books/sync/cancel", {}),

  /** Hold a file out of the whole-book sweep (nightly / every-file), or release
   * it. Held, it runs only when named. */
  setFileHold: (fileId: string, hold: boolean) =>
    post<{ file_id: string; hold_from_sweep: boolean }>(
      `/api/books/files/${fileId}/hold`,
      { hold },
    ),

  /**
   * Force a full re-backfill of one file's transactions, then sweep it. The
   * only way to give existing header-only history its lines -- the incremental
   * sync never re-fetches an unchanged transaction. Builder-only; requires a
   * file id (422 without one, since resetting every file would hold QuickBooks
   * for hours).
   */
  resetBackfill: (fileId: string) =>
    post<{ sweep: LedgerSweep; note: string }>("/api/books/sync", {
      file_id: fileId,
      reset: true,
    }),

  cases: (status?: "open" | "snoozed" | "dismissed") =>
    get<{
      cases: ExceptionCase[];
      counts: CaseCounts;
      reopen_margin_pct: number;
      max_snooze_days: number;
    }>(`/api/cases${status ? `?status=${status}` : ""}`),

  caseHistory: (limit = 100) =>
    get<{ cases: ClosedCase[] }>(`/api/cases/history?limit=${limit}`),

  /**
   * Snooze it, dismiss it, or put it back on the open pile.
   *
   * Addressed by `case_type` + `subject_id`, never by a case id, because a
   * condition that became true four minutes ago has no case row yet and the
   * screen is already showing it. The server materialises one.
   */
  decideCase: (body: {
    case_type:
      | "floor_breach"
      | "connection_problem"
      | "missing_payment"
      | "unreconciled_balance";
    subject_id: string;
    action: "snooze" | "dismiss" | "reopen";
    note?: string;
    remind_at?: string;
  }) => post<ExceptionCase>("/api/cases/decide", body),
  positionHistory: (days: number, scope: { group?: string; category?: string } = {}) => {
    const query = new URLSearchParams({ days: String(days) });
    if (scope.group) query.set("group", scope.group);
    if (scope.category) query.set("category", scope.category);
    return get<PositionHistory>(`/api/position/history?${query.toString()}`);
  },

  /**
   * Cash today and what moved today, over a saved group or a category.
   * Neither given is the whole reach -- the dashboard's own figure.
   */
  scopedPosition: (scope: { group?: string; category?: string } = {}) => {
    const query = new URLSearchParams();
    if (scope.group) query.set("group", scope.group);
    if (scope.category) query.set("category", scope.category);
    const qs = query.toString();
    return get<ScopedPosition>(qs ? `/api/position/scoped?${qs}` : "/api/position/scoped");
  },

  /**
   * Available cash for a scope: cash − cards owed − payables due by a horizon.
   * Components come back; the screen does the subtraction so switching the
   * horizon is instant. Obligations are not folded in yet (`built` is false).
   */
  availableCash: (
    scope: { group?: string; category?: string } = {},
    days?: number | null,
  ) => {
    const query = new URLSearchParams();
    if (scope.group) query.set("group", scope.group);
    if (scope.category) query.set("category", scope.category);
    // The configurable horizon: how far out to count what is owed/expected.
    // Omitted = "All open" (the whole total).
    if (days !== null && days !== undefined) query.set("days", String(days));
    const qs = query.toString();
    return get<AvailableCash>(
      qs ? `/api/position/available?${qs}` : "/api/position/available",
    );
  },

  groups: () =>
    get<{ count: number; groups: EntityGroup[]; categories: { key: string; label: string }[] }>(
      "/api/groups",
    ),
  createGroup: (body: GroupBody) =>
    post<{ id: string; name: string; members_total: number }>("/api/groups", body),
  updateGroup: (id: string, body: GroupBody) =>
    put<{ id: string; name: string; members_total: number }>(`/api/groups/${id}`, body),
  retireGroup: (id: string) => del<void>(`/api/groups/${id}`),

  entities: () => get<{ count: number; entities: Entity[] }>("/api/entities"),
  entity: (id: string) => get<EntityDetail>(`/api/entities/${id}`),

  accounts: (entityId?: string) =>
    get<{ count: number; accounts: Account[] }>(
      entityId
        ? `/api/accounts?entity_id=${encodeURIComponent(entityId)}`
        : "/api/accounts",
    ),
  account: (id: string) => get<AccountDetail>(`/api/accounts/${id}`),
  /**
   * Soft-retire one account: it drops off every list and total, but its row and
   * whole history stay. Reach-gated (you can retire an account you can see), not
   * builder-only. `connection_now_empty` is true when the login this account
   * hung on now carries nothing live, so the caller can offer retiring the login
   * as the next step.
   */
  retireAccount: (id: string) =>
    del<{
      id: string;
      retired: true;
      name: string;
      connection_id: string | null;
      login_label: string | null;
      connection_now_empty: boolean;
    }>(`/api/accounts/${id}`),
  accountTransactions: (id: string, includeRemoved = false) =>
    get<{
      count: number;
      transactions: Transaction[];
      summary: TransactionSummary;
    }>(
      `/api/accounts/${id}/transactions${includeRemoved ? "?include_removed=true" : ""}`,
    ),
  accountInvestments: (id: string, movementsOnly = false) =>
    get<{
      count: number;
      investments: InvestmentTransaction[];
      summary: InvestmentSummary;
    }>(
      `/api/accounts/${id}/investments${movementsOnly ? "?movements_only=true" : ""}`,
    ),
  accountHistory: (id: string) =>
    get<{ count: number; observations: Observation[] }>(
      `/api/accounts/${id}/history`,
    ),
  setApprover: (
    id: string,
    approver_user_id: string | null,
    note?: string,
  ) =>
    patch<{ account: AccountDetail; warning: string | null }>(
      `/api/accounts/${id}/approver`,
      { approver_user_id, note },
    ),
  setFloor: (id: string, cash_floor_cents: number | null, note?: string) =>
    patch<AccountDetail>(`/api/accounts/${id}/floor`, {
      cash_floor_cents,
      note: note ?? null,
    }),
  /** Set or clear the invested anchor inside a brokerage account (073).
   * `null` clears it (distinct from zero). Investment accounts only -- the
   * server refuses it on a cash account. Available cash = balance - this. */
  setInvested: (
    id: string,
    manual_invested_cents: number | null,
    note?: string,
  ) =>
    patch<AccountDetail>(`/api/accounts/${id}/invested`, {
      manual_invested_cents,
      note: note ?? null,
    }),
  refresh: (accountId?: string) =>
    post<RefreshResult>("/api/accounts/refresh", {
      account_id: accountId ?? null,
    }),
  /**
   * Refresh one bank login directly, by its connection id. What the
   * Connections screen sends to retry a bank in place -- you are looking at
   * the broken login itself and have no account in hand.
   */
  refreshConnection: (connectionId: string) =>
    post<RefreshResult>("/api/accounts/refresh", {
      connection_id: connectionId,
    }),
  /**
   * Begin repairing a broken bank login. Returns a Plaid Hosted Link URL the
   * person opens in a new tab and signs into their bank through. Update mode,
   * so it repairs the existing connection and never spends an Item slot; after
   * they finish, a {@link refreshConnection} clears the error.
   */
  reauthConnection: (connectionId: string) =>
    post<{
      hosted_link_url: string | null;
      expiration: string | null;
      mode: string;
    }>("/api/connections/link-token", {
      connection_id: connectionId,
      hosted: true,
    }),
  /**
   * Re-open the bank's account picker for a login already held, so the holder
   * can add accounts that were left unchecked at first link (or drop ones they
   * no longer want to share). Returns a Plaid Hosted Link URL — update mode with
   * account selection, so it spends no Item slot. After they finish at the bank,
   * a {@link rescanConnection} brings the newly-shared accounts in. This is the
   * only path that works when an account was de-selected: the bank stops sharing
   * it entirely, so a rescan on its own can never see it.
   */
  editAccountsLink: (connectionId: string) =>
    post<{
      hosted_link_url: string | null;
      expiration: string | null;
      mode: string;
    }>("/api/connections/link-token", {
      connection_id: connectionId,
      hosted: true,
      select_accounts: true,
    }),
  /**
   * Re-scan an existing login for accounts opened since it was linked. A
   * {@link refreshConnection} only re-reads balances on accounts already
   * linked; this asks the bank what accounts exist now, so a newly-opened
   * account gets linked if it matches a registry row, or lands in Held for
   * review. Spends no Item slot.
   */
  rescanConnection: (connectionId: string) =>
    post<{
      connection_id: string;
      institution: string;
      login_label: string;
      accounts_linked: number;
      accounts_already_linked: number;
      accounts_held_for_review: number;
      notes: string[];
    }>(`/api/connections/${connectionId}/rescan`, {}),
  /**
   * Pull this login's transactions now — the manual twin of the pull a
   * reconnect triggers automatically. Used to finish a first pull that did not
   * land (a bank that did not answer on reconnect), which the scheduled sweep
   * will not retry on its own. Spends a billed transactions call; a never-synced
   * login pulls its whole history. `spent`/`failed` say what happened.
   */
  pullTransactions: (connectionId: string) =>
    post<{
      spent: boolean;
      failed: boolean;
      note: string;
      full_history: boolean;
      counts: { added: number } | null;
      deduplicated?: number;
    }>(`/api/connections/${connectionId}/pull-transactions`, {}),
  /**
   * Retire an emptied bank login so it drops off the Connections list. The
   * server refuses (409) if it still carries any live account — only an inert
   * husk, every account gone, can be retired — and best-effort tells Plaid to
   * forget the Item. Builder only. `note` carries a Plaid hiccup when the local
   * retire went through but `/item/remove` did not.
   */
  retireConnection: (connectionId: string) =>
    del<{
      id: string;
      retired: true;
      login_label: string;
      note: string | null;
    }>(`/api/connections/${connectionId}`),
  /** Relabel a login. Reach-gated; touches no money. 409 if the name clashes. */
  renameConnection: (connectionId: string, loginLabel: string) =>
    post<{ id: string; login_label: string; renamed: boolean }>(
      `/api/connections/${connectionId}/rename`,
      { login_label: loginLabel },
    ),
  /** Preview replacing a broken login with a new one. Writes nothing. */
  planMigration: (connectionId: string, targetConnectionId: string) =>
    post<MigrationPlan>(`/api/connections/${connectionId}/migrate`, {
      target_connection_id: targetConnectionId,
      dry_run: true,
    }),
  /** Execute the move: the broken login's accounts take over the new login's
   *  items. Builder only; writes an audit event. Retiring the emptied source
   *  stays a separate step. */
  migrateConnection: (connectionId: string, targetConnectionId: string) =>
    post<{
      moved: number;
      stayed_on_source: number;
      new_to_register: number;
      ambiguous: number;
      source_empty: boolean;
      source_login: string;
      target_login: string;
    }>(`/api/connections/${connectionId}/migrate`, {
      target_connection_id: targetConnectionId,
      dry_run: false,
    }),
  /**
   * Begin a NEW bank connection. Create mode (no connection_id), hosted, so it
   * returns a Plaid Hosted Link URL the person opens in a new tab and signs
   * into their bank through — the same two-step shape as {@link reauthConnection},
   * except this one SPENDS an Item. At the Item cap the server answers 409
   * before Plaid is ever asked to create one, so no slot is burned on a failed
   * attempt. Keep the returned `link_token`; {@link adoptConnection} needs it.
   */
  startNewConnection: () =>
    post<{
      link_token: string;
      hosted_link_url: string | null;
      expiration: string | null;
      mode: string;
    }>("/api/connections/link-token", { hosted: true }),
  /**
   * Finish a new connection after the person has signed in on the Hosted Link.
   * Hosted Link never hands the browser a public_token, so the server reads the
   * link session back to collect the Item and matches its accounts exactly like
   * a first link — new accounts in the registry are linked, the rest land in
   * Held for review. Idempotent: pressing it again after it worked changes
   * nothing. Answers 409 ("not back yet") if the sign-in is not finished.
   */
  adoptConnection: (linkToken: string, loginLabel: string) =>
    post<{
      connection_id: string;
      institution: string;
      login_label: string;
      accounts_linked: number;
      accounts_already_linked: number;
      accounts_held_for_review: number;
      notes: string[];
    }>("/api/connections/adopt", {
      link_token: linkToken,
      login_label: loginLabel,
    }),
  /**
   * Add a non-Plaid account to the registry (builder only). The balance is not
   * set here — it is typed afterwards on /readings, the same door Crown Bank and
   * the Israeli account use. Give either an existing `institution_id` or a
   * `new_institution` to create (always manual), never both.
   */
  createAccount: (body: {
    entity_id: string;
    institution_id?: string;
    new_institution?: { name: string; staleness_warn_minutes?: number };
    name: string;
    account_type:
      | "checking"
      | "business_checking"
      | "credit_card"
      | "line_of_credit";
    last4?: string | null;
    currency?: "USD" | "ILS";
    cash_floor_cents?: number | null;
    approver_user_id?: string | null;
    phi_adjacent?: boolean;
    note?: string | null;
  }) =>
    post<{
      id: string;
      name: string;
      account_type: string;
      last4: string | null;
      currency: string;
      phi_adjacent: boolean;
      entity_name: string;
      institution_name: string;
    }>("/api/accounts", body),
  recordReading: (
    id: string,
    body: {
      balance_cents: number;
      available_cents: number | null;
      /** ISO 8601 **with** an offset. The server refuses a naive timestamp. */
      as_of: string;
      source: string;
      note?: string | null;
      /**
       * Currency entry (074). For a USD account, omit both. For a non-USD
       * account, `entered_currency` says which currency the figure above is in
       * ("ILS" to convert, "USD" for a one-off dollar figure) and
       * `fx_rate_to_usd` is the rate a native figure is converted at. The server
       * requires the rate for a native-currency reading and ignores both on a
       * USD account.
       */
      entered_currency?: "USD" | "ILS";
      fx_rate_to_usd?: number | null;
    },
  ) => post<RecordedReading>(`/api/accounts/${id}/reading`, body),

  /**
   * Record several typed balances in one go — one moment and one source for
   * all of them. The batch form behind a down or manual bank: open the portal
   * once, type every balance, record them together. Each entry is written as
   * its own reading server-side, so the per-account provenance and the `as_of`
   * ranking are identical to {@link api.recordReading}; this only spares
   * re-entering the date and source per account.
   */
  recordReadings: (body: {
    /** ISO 8601 **with** an offset. The server refuses a naive timestamp. */
    as_of: string;
    source: string;
    note?: string | null;
    entries: {
      account_id: string;
      balance_cents: number;
      available_cents: number | null;
      /**
       * Shekel→USD rate for a non-USD account's row (074). The rate is per
       * account because one portal screen can hold both a dollar and a shekel
       * account; the figure typed is taken to be in the account's own currency.
       * Required for a non-USD account, ignored for a USD one.
       */
      fx_rate_to_usd?: number | null;
    }[];
  }) => post<RecordedReadings>("/api/accounts/readings", body),

  institutions: () =>
    get<{ count: number; institutions: Institution[] }>("/api/institutions"),

  connections: () =>
    get<{ count: number; connections: Connection[] }>("/api/connections"),
  coverage: () => get<Coverage>("/api/connections/coverage"),
  held: () => get<{ count: number; held: HeldAccount[] }>("/api/connections/held"),

  /** What each feed would cost to refresh. Calls no bank and writes nothing. */
  syncState: () => get<SyncState>("/api/sync"),

  /**
   * Pull one feed for one login, now. **This one spends money.**
   *
   * `for_real` is the `--for-real` the CLI tools demand and it defaults to
   * false here for the same reason it does there: a call that forgets it must
   * answer with what a sync WOULD do rather than doing it.
   *
   * `allow_first_sync` is the second yes, and it is only needed for the one
   * call that asks a bank for everything it has ever held.
   */
  runSync: (body: {
    feed: SyncFeed;
    login_label: string;
    for_real?: boolean;
    allow_first_sync?: boolean;
    reset_cursor?: boolean;
  }) => post<SyncOutcome>("/api/sync/run", body),
  /**
   * Take a backup now, the way the 02:30 slot does. Builder-only. The
   * response is the `backup_run` row's facts; 409 carries the refusal.
   */
  runBackup: () => post<BackupNow>("/api/sync/backup", {}),
  resolveHeld: (
    id: string,
    body: {
      resolution: "matched" | "out_of_scope";
      account_id?: string | null;
      note?: string | null;
    },
  ) =>
    post<{ id: string; resolution: string; detail: string }>(
      `/api/connections/held/${id}/resolve`,
      body,
    ),

  /**
   * Resolve a held account by creating a BRAND-NEW registry account for it and
   * linking it, in one step — the third answer alongside Match and Out of scope.
   * The institution is the held account's own bank (not chosen); the entity is
   * reach-gated the same as Match. `last4` defaults to the held mask when
   * omitted. Returns the new account's id, with the held row already resolved.
   */
  createHeldAccount: (
    heldId: string,
    body: {
      entity_id: string;
      name: string;
      account_type:
        | "checking"
        | "business_checking"
        | "credit_card"
        | "line_of_credit";
      last4?: string | null;
      currency?: "USD" | "ILS";
      phi_adjacent?: boolean;
      note?: string | null;
    },
  ) =>
    post<{
      id: string;
      account_id: string;
      resolution: string;
      detail: string;
    }>(`/api/connections/held/${heldId}/create-account`, body),

  activity: (params: { limit?: number; before?: number; type?: string } = {}) => {
    const search = new URLSearchParams();
    if (params.limit) search.set("limit", String(params.limit));
    if (params.before) search.set("before", String(params.before));
    if (params.type) search.set("type", params.type);
    const query = search.toString();
    return get<{
      count: number;
      events: ActivityEvent[];
      next_before: number | null;
    }>(query ? `/api/activity?${query}` : "/api/activity");
  },

  people: () =>
    get<{ count: number; people: Person[]; may_edit: boolean }>("/api/people"),
  /** Grant or revoke one level on one entity for one person. Managers only. */
  changeAccess: (userId: string, body: { entity_id: string; level: "read" | "approve"; on: boolean }) =>
    post<{ sees: boolean; approves: boolean }>(`/api/people/${userId}/access`, body),
  /** An edit saved in one go: every change lands, or none does. */
  changeAccessBatch: (
    userId: string,
    changes: { entity_id: string; level: "read" | "approve"; on: boolean }[],
  ) =>
    post<Record<string, { sees: boolean; approves: boolean }>>(
      `/api/people/${userId}/access/batch`,
      { changes },
    ),

  /**
   * Creating people and changing how they sign in, from the People screen
   * (037, `api/people_admin.py`). Managers only, and `builder` is not an
   * offered role nor a target -- that tier stays on the command line.
   */
  addPerson: (body: { email: string; full_name: string; role: ManageableRole }) =>
    post<{ id: string; email: string; full_name: string; role: ManageableRole }>(
      "/api/people",
      body,
    ),
  /** Generate a one-time password and read it back ONCE. Hand it over directly. */
  setTempPassword: (userId: string) =>
    post<{ password: string; full_name: string; email: string }>(
      `/api/people/${userId}/password`,
    ),
  /** Change a person's role, email and/or active state. Any subset. */
  editPerson: (
    userId: string,
    body: { role?: ManageableRole; email?: string; is_active?: boolean },
  ) => patch<{ ok: true }>(`/api/people/${userId}`, body),
  /** End every live session a person holds, now. */
  signOutPerson: (userId: string) =>
    post<{ ended: number }>(`/api/people/${userId}/sign-out`),
  /** Remove a person who has never acted. 409 if they are in the audit trail. */
  deletePerson: (userId: string) =>
    del<{ deleted: true; full_name: string }>(`/api/people/${userId}`),
  /**
   * Subscribe or unsubscribe one person from an alert (064). Managers only.
   * The mail they would get is already reach-scoped, so this is just told-or-not.
   */
  setPersonAlert: (userId: string, on: boolean, eventType = "floor_breach") =>
    post<{ ok: true; event_type: string; user_id: string; subscribed: boolean }>(
      `/api/people/${userId}/alerts`,
      { on, event_type: eventType },
    ),

  // -------------------------------------------------------------------------
  // Alerts (063/064): who gets emailed when a cash account drops below its
  // floor. Read-only here and by anyone signed in; subscriptions are toggled
  // per person on the People screen (setPersonAlert), a manager act.
  // -------------------------------------------------------------------------
  alertRecipients: (eventType = "floor_breach") =>
    get<AlertRecipients>(
      `/api/alerts/recipients?event_type=${encodeURIComponent(eventType)}`,
    ),

  // -------------------------------------------------------------------------
  // Snapshot emails (096): who receives each group's daily balance snapshot,
  // moved out of SNAPSHOT_JOBS.to into the hub. Read by anyone signed in;
  // writes are an access-manager act. A recipient is a user OR a free email.
  // -------------------------------------------------------------------------
  snapshotRecipients: () =>
    get<SnapshotRecipients>("/api/snapshot-recipients"),
  addSnapshotUser: (groupId: string, userId: string) =>
    post<{ ok: true; id: string }>("/api/snapshot-recipients/users", {
      group_id: groupId,
      user_id: userId,
    }),
  addSnapshotEmail: (groupId: string, email: string, label?: string) =>
    post<{ ok: true; id: string }>("/api/snapshot-recipients/emails", {
      group_id: groupId,
      email,
      label: label || null,
    }),
  setSnapshotRecipientEnabled: (id: string, on: boolean) =>
    post<{ ok: true; id: string; enabled: boolean }>(
      `/api/snapshot-recipients/${id}/enabled`,
      { on },
    ),
  removeSnapshotRecipient: (id: string) =>
    del<{ ok: true; id: string; removed: boolean }>(
      `/api/snapshot-recipients/${id}`,
    ),

  counterparties: (kind?: string) =>
    get<{
      kind: string | null;
      note: string | null;
      error?: string;
      totals: CounterpartyTotal[];
      parties: Counterparty[];
    }>(kind ? `/api/counterparties?kind=${encodeURIComponent(kind)}` : "/api/counterparties"),

  internalTransfers: () =>
    get<{
      transfers: InternalTransfer[];
      pairs_found: number;
      inferred_only: number;
    }>("/api/counterparties/internal"),

  /**
   * Payments out of pattern for their counterparty (H4).
   *
   * Its own call rather than part of `/api/dashboard`: the query walks a year
   * of payments to build the baselines, and a band that cannot be computed
   * should fail on its own rather than take the exceptions band with it.
   */
  unusualAmounts: (days = 30) =>
    get<UnusualAmounts>(`/api/dashboard/unusual?days=${days}`),

  searchTransactions: (filters: TransactionFilters = {}) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      // An empty box is not a filter. Sending `counterparty=` would ask the
      // server to match the empty string rather than to stop filtering.
      if (value === undefined || value === null || value === "") continue;
      query.set(key, String(value));
    }
    const qs = query.toString();
    return get<TransactionSearch>(
      qs ? `/api/transactions?${qs}` : "/api/transactions",
    );
  },

  /**
   * Say what a transaction was for, queue it for a person, or exclude it (T4).
   *
   * ⚠️ There is no `post` action and there must not be. `posted` is written by
   * the posting path after a successful write to QuickBooks AND a read-back; a
   * row marked posted that is not actually there is excluded by the next run
   * as already-posted, and the transaction is lost from both sides with
   * nothing anywhere saying so.
   *
   * Needs APPROVE on the entity, not read.
   */
  classifyTransaction: (
    transactionId: string,
    body: {
      action: "classify" | "approve" | "review" | "exclude" | "clear";
      file_id?: string | null;
      account_qbd_id?: string | null;
      excluded_reason?: string | null;
      /** `classify` only: also categorize every other uncategorized transaction
       *  from the same vendor, to the same account. */
      apply_to_vendor?: boolean;
    },
  ) =>
    put<{ state: string; count?: number; vendor?: string | null }>(
      `/api/transactions/${encodeURIComponent(transactionId)}/classification`,
      body,
    ),

  /**
   * Apply ONE decision to many bank transactions at once (Chaim 2026-10-29).
   * Each row is still checked for reach and pending/posted on the server, so a
   * bulk action can never touch a row the person could not touch alone; the
   * reply says how many changed and lists what it skipped and why.
   */
  bulkClassify: (body: {
    transaction_ids: string[];
    action: "classify" | "approve" | "review" | "exclude" | "clear";
    file_id?: string | null;
    account_qbd_id?: string | null;
    excluded_reason?: string | null;
    /** `approve` only: the account to sign off for lines whose suggestion lives
     *  only in their candidates (no saved account), keyed by transaction id. A
     *  row absent here (or one with a saved account) signs off on the account
     *  already on its line. */
    approvals?: Record<string, { file_id: string; account_qbd_id: string }>;
  }) =>
    post<{ updated: number; skipped: Array<{ id: string; reason: string }> }>(
      "/api/transactions/classification/bulk",
      body,
    ),

  /**
   * The other lines a vendor-wide apply would categorise — same entity, same
   * direction, posted only (Chaim §6). Read-only; drives the confirm step
   * before any bulk write.
   */
  vendorSiblings: (transactionId: string) =>
    get<{
      vendor: string | null;
      lines: Array<{
        id: string;
        posted_on: string | null;
        amount_cents: number;
        counterparty_name: string | null;
        account_name: string | null;
        last4: string | null;
        entity_name: string | null;
      }>;
    }>(`/api/transactions/${encodeURIComponent(transactionId)}/vendor-siblings`),

  /**
   * Run the categorization agent over the backlog (builder only). It books what
   * history already knows, queues what it does not, and excludes what is
   * already in QuickBooks. ⚠️ It posts nothing — `proposed`/`in_review` only.
   */
  runClassification: (fileId?: string | null) =>
    post<{
      cutoff: string;
      totals: {
        categorized: number;
        proposed: number;
        in_review: number;
        excluded: number;
        left_alone: number;
        seen: number;
      };
      files: Array<{
        file_id: string;
        file_name: string;
        categorized: number;
        proposed: number;
        in_review: number;
        excluded: number;
        left_alone: number;
        seen: number;
      }>;
    }>("/api/classification/run", fileId ? { file_id: fileId } : {}),

  // -------------------------------------------------------------------------
  // The Rules / approval screen (Chaim §4, §6.2). Reads the rule tables and
  // the learned candidates; the four mutations bless / re-point / add / remove
  // a rule. ⚠️ None of this posts to QuickBooks — it authors Orbit's own rule
  // tables, which is the editing the pilot reserves to the operator. Blessing a
  // rule (`signed_off`) is what flips its lines from one-click to auto.
  // -------------------------------------------------------------------------
  classificationRules: (fileId: string) =>
    get<ClassificationRules>(
      `/api/classification/rules?file_id=${encodeURIComponent(fileId)}`,
    ),
  signClassificationRule: (body: {
    file_id: string;
    rule_type: RuleType;
    rule_id: string;
    signed: boolean;
  }) => post<{ ok: boolean; signed_off: boolean }>("/api/classification/rules/sign", body),
  setClassificationRuleAccount: (body: {
    file_id: string;
    rule_type: RuleType;
    rule_id: string;
    full_name: string;
    side?: "in" | "out";
  }) => post<{ ok: boolean; full_name: string }>("/api/classification/rules/account", body),
  approveClassificationCandidate: (body: {
    file_id: string;
    match_text: string;
    target_account_full_name: string;
  }) =>
    post<{ ok: boolean; id: string; match_text: string }>(
      "/api/classification/rules/approve-candidate",
      body,
    ),
  setClassificationAutoApprove: (body: { value: string; file_id?: string }) =>
    post<ClassificationAutoApproveResult>("/api/classification/auto-approve", body),
  deleteClassificationRule: (ruleType: RuleType, ruleId: string, fileId: string) =>
    del<{ ok: boolean }>(
      `/api/classification/rules/${ruleType}/${ruleId}?file_id=${encodeURIComponent(fileId)}`,
    ),

  relatedParties: () =>
    get<{
      review: RelatedParty[];
      gross_cents: number;
      transactions: number;
    }>("/api/counterparties/related"),

  // --- Feedback ---------------------------------------------------------------
  feedback: (all = false) =>
    get<{ count: number; open: number; items: FeedbackItem[]; may_triage: boolean }>(
      all ? "/api/feedback?all=true" : "/api/feedback",
    ),
  fileFeedback: (body: { title: string; body?: string; kind: FeedbackKind; page?: string }) =>
    post<FeedbackItem>("/api/feedback", body),
  /** The image as its own request, raw bytes, so the JSON route stays JSON. */
  attachScreenshot: (id: string, file: Blob) =>
    request<FeedbackItem>(`/api/feedback/${id}/screenshot`, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type },
    }),
  feedbackItem: (id: string) =>
    get<{ item: FeedbackItem; comments: FeedbackComment[] }>(`/api/feedback/${id}`),
  commentOnFeedback: (id: string, body: string) =>
    post<FeedbackComment>(`/api/feedback/${id}/comments`, { body }),
  moveFeedback: (id: string, status: FeedbackStatus) =>
    patch<FeedbackItem>(`/api/feedback/${id}/status`, { status }),

  // --- Payables -------------------------------------------------------------
  vendors: (includeArchived = false) =>
    get<{
      count: number;
      vendors: Vendor[];
      without_bank_details: number;
      pending_changes: number;
      may_approve: boolean;
    }>(`/api/vendors${includeArchived ? "?include_archived=true" : ""}`),

  vendor: (id: string) =>
    get<{
      vendor: Vendor;
      bank_details: VendorBankDetail[];
      invoices: Invoice[];
    }>(`/api/vendors/${id}`),

  createVendor: (body: {
    name: string;
    legal_name?: string | null;
    w9_status?: string;
    tax_id_last4?: string | null;
  }) => post<{ id: string; name: string }>("/api/vendors", body),

  editVendor: (
    id: string,
    body: {
      name: string;
      legal_name?: string | null;
      w9_status?: string;
      tax_id_last4?: string | null;
    },
  ) => patch<{ id: string; name: string }>(`/api/vendors/${id}`, body),

  archiveVendor: (id: string, reason?: string | null) =>
    post<{ id: string; status: string }>(`/api/vendors/${id}/archive`, {
      reason: reason ?? null,
    }),

  reactivateVendor: (id: string) =>
    post<{ id: string; status: string }>(`/api/vendors/${id}/reactivate`),

  proposeBankDetail: (
    vendorId: string,
    body: {
      rail?: Rail;
      institution_name?: string | null;
      account_last4?: string | null;
      routing_number?: string | null;
      wire_routing_number?: string | null;
      swift_code?: string | null;
      account_kind?: string;
      handle?: string | null;
      payee_name?: string | null;
      source?: string;
      announced_change?: boolean;
      change_notice_text?: string | null;
    },
  ) => post<{ id: string; state: string; rail: Rail }>(
    `/api/vendors/${vendorId}/bank-details`,
    body,
  ),

  invoices: (filters: { status?: string; entity_id?: string } = {}) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (!value) continue;
      query.set(key, value);
    }
    const qs = query.toString();
    return get<{ invoices: Invoice[]; totals: InvoiceTotals }>(
      qs ? `/api/invoices?${qs}` : "/api/invoices",
    );
  },

  createInvoice: (body: {
    entity_id: string;
    vendor_id: string;
    amount_cents: number;
    invoice_number?: string | null;
    invoice_date?: string | null;
    due_date?: string | null;
  }) => post<{ id: string; status: string }>("/api/invoices", body),

  /**
   * Read a bill off its file. The file is the raw request body with its own
   * media type as Content-Type — not JSON — so `request` is called directly.
   * Stores nothing; the answer fills the form and a person commits it.
   */
  extractInvoice: (file: File) =>
    request<InvoiceExtraction>("/api/invoices/extract", {
      method: "POST",
      body: file,
      headers: { "Content-Type": file.type },
    }),

  submitInvoice: (id: string) =>
    post<{ id: string; state: string }>(`/api/invoices/${id}/submit`),

  /** One bill plus its files, for the detail drawer (102). 404 when the reader
   *  cannot see it. */
  invoice: (id: string) =>
    get<{ invoice: Invoice; documents: InvoiceDocument[] }>(`/api/invoices/${id}`),

  /**
   * Send several bills to an approver at once (Sarah 2026-11). Per-bill
   * outcomes come back: a bill that can't be sent is reported with its reason,
   * not forced, and does not sink the rest.
   */
  batchSubmitInvoices: (invoiceIds: string[]) =>
    post<{
      results: { id: string; ok: boolean; error?: string }[];
      sent: number;
      total: number;
    }>("/api/invoices/batch-submit", { invoice_ids: invoiceIds }),

  /**
   * Withdraw a bill that should not be paid, with the reason on the record
   * (Sarah 2026-11). A soft delete: the bill is marked void, never removed, so
   * the audit trail and the count of withdrawn bills survive. Only while the
   * bill is still open and off any payment.
   */
  voidInvoice: (id: string, reason: string) =>
    post<{ id: string; status: string }>(`/api/invoices/${id}/void`, { reason }),

  /** The files on one bill, newest first (102). */
  invoiceDocuments: (id: string) =>
    get<{ count: number; documents: InvoiceDocument[] }>(
      `/api/invoices/${id}/documents`,
    ),

  /**
   * Attach the file a bill came on. Raw request body with its own media type as
   * Content-Type — the same convention as extractInvoice and the obligation
   * documents, not multipart — and the filename on the query string.
   */
  uploadInvoiceDocument: (id: string, file: File) =>
    request<InvoiceDocument>(
      `/api/invoices/${id}/documents?filename=${encodeURIComponent(file.name)}`,
      { method: "POST", body: file, headers: { "Content-Type": file.type } },
    ),

  /** Remove a file from a bill. */
  deleteInvoiceDocument: (id: string, documentId: string) =>
    del<{ id: string; deleted: boolean }>(
      `/api/invoices/${id}/documents/${documentId}`,
    ),

  /** The URL the browser downloads a bill's file from. The session cookie rides
   *  with the navigation; the server sets the attachment disposition. */
  invoiceDocumentUrl: (id: string, documentId: string) =>
    `/api/invoices/${id}/documents/${documentId}`,

  /**
   * Record a person's judgement that one open bill is a second copy of another
   * (021). The original must be the same vendor and company and not itself be
   * voided; a reason is required and kept on the record. The copy is voided so
   * it drops off open payables and can never be paid — nothing is sent, nothing
   * is deleted, and the original is left exactly as it is.
   */
  markDuplicate: (id: string, body: { original_id: string; reason: string }) =>
    post<{ id: string; status: string; duplicate_of: string }>(
      `/api/invoices/${id}/mark-duplicate`,
      body,
    ),

  /** Record (or clear, with []) a bill's installment schedule (050). */
  setInstallments: (
    id: string,
    installments: { amount_cents: number; due_date: string }[],
  ) =>
    post<{
      invoice_id: string;
      installments: { seq: number; amount_cents: number; due_date: string }[];
    }>(`/api/invoices/${id}/installments`, { installments }),

  /**
   * Vendor credits the reader can see (051). One shape serves three surfaces:
   * the whole history (no filter), a vendor's credits, and the prompt case —
   * what is still open for a vendor+entity about to be billed.
   */
  creditNotes: (
    q: { vendor_id?: string; entity_id?: string; open_only?: boolean } = {},
  ) => {
    const query = new URLSearchParams();
    if (q.vendor_id) query.set("vendor_id", q.vendor_id);
    if (q.entity_id) query.set("entity_id", q.entity_id);
    if (q.open_only) query.set("open_only", "true");
    const qs = query.toString();
    return get<{
      count: number;
      credit_notes: CreditNote[];
      open_remaining_cents: number;
    }>(qs ? `/api/credit-notes?${qs}` : "/api/credit-notes");
  },

  /** Capture a standalone vendor credit. Creates nothing payable. */
  createCreditNote: (body: {
    entity_id: string;
    vendor_id: string;
    amount_cents: number;
    credit_number?: string | null;
    issued_date?: string | null;
  }) => post<{ id: string; status: string }>("/api/credit-notes", body),

  /** Net a credit against a bill. Records the act; sends no money. */
  applyCredit: (
    creditNoteId: string,
    body: { invoice_id: string; applied_cents: number },
  ) =>
    post<{ id: string; applied_cents: number }>(
      `/api/credit-notes/${creditNoteId}/apply`,
      body,
    ),

  /** Standing monthly-bill templates the reader can see (058). */
  recurringBills: (q: { entity_id?: string } = {}) => {
    const query = new URLSearchParams();
    if (q.entity_id) query.set("entity_id", q.entity_id);
    const qs = query.toString();
    return get<{ count: number; recurring_bills: RecurringBill[] }>(
      qs ? `/api/recurring-bills?${qs}` : "/api/recurring-bills",
    );
  },

  /**
   * A standing instruction for a vendor who sends no invoice (058). It makes
   * one bill a month for a fixed amount; it is not a bill and moves no money.
   */
  createRecurringBill: (body: {
    entity_id: string;
    vendor_id: string;
    amount_cents: number;
    day_of_month: number;
    memo?: string | null;
    starts_on?: string | null;
    ends_on?: string | null;
    /** How many times to recur before it waits for a decision (103). Omit to
     *  run until paused or ended. */
    occurrence_limit?: number | null;
  }) => post<{ id: string; status: string }>("/api/recurring-bills", body),

  /** Pause, resume (active) or end a template. Ending is permanent (058). */
  setRecurringStatus: (id: string, status: "active" | "paused" | "ended") =>
    post<{ id: string; status: string }>(`/api/recurring-bills/${id}/status`, {
      status,
    }),

  /**
   * Continue or shorten a template's run (103): set how many bills it should
   * make in total, or pass null to run without a limit. "Run N more" sends
   * (made so far + N). Stopping for good is still setRecurringStatus("ended").
   */
  setRecurringOccurrenceLimit: (id: string, occurrence_limit: number | null) =>
    post<{ id: string; occurrence_limit: number | null }>(
      `/api/recurring-bills/${id}/occurrence-limit`,
      { occurrence_limit },
    ),

  /** Obligations the reader can see (059), each loan carrying its return leg. */
  obligations: (q: { entity_id?: string; status?: string } = {}) => {
    const query = new URLSearchParams();
    if (q.entity_id) query.set("entity_id", q.entity_id);
    if (q.status) query.set("status", q.status);
    const qs = query.toString();
    return get<{ count: number; obligations: Obligation[] }>(
      qs ? `/api/obligations?${qs}` : "/api/obligations",
    );
  },

  /** Record an expected cash event. Moves no money; counts in the forecast. */
  createObligation: (body: {
    entity_id: string;
    kind:
      | "investment"
      | "loan_given"
      | "loan_return"
      | "income"
      | "investment_return"
      | "other";
    amount_cents: number;
    due_date: string;
    direction?: "outflow" | "inflow" | null;
    counterparty?: string | null;
    note?: string | null;
    return_amount_cents?: number | null;
    return_on?: string | null;
    account_id?: string | null;
    investment_name?: string | null;
    investment_category_id?: string | null;
    investment_contact_name?: string | null;
    investment_contact_phone?: string | null;
    investment_contact_email?: string | null;
  }) => post<{ id: string; status: string }>("/api/obligations", body),

  /** The add-as-you-go investment categories (086), for the Category picklist. */
  investmentCategories: () =>
    get<{ count: number; categories: InvestmentCategory[] }>(
      "/api/investment-categories",
    ),

  /** Add a category (or get the matching existing one). It persists for the
   *  next investment. */
  createInvestmentCategory: (name: string) =>
    post<InvestmentCategory>("/api/investment-categories", { name }),

  /**
   * Record a commitment drawn in tranches (066) — e.g. 1.3M split into dated
   * 300K draws. Each tranche becomes a row on its own date and counts in the
   * forecast then; the total is the sum of the tranches.
   */
  createObligationSchedule: (body: {
    entity_id: string;
    kind: "investment" | "loan_given" | "income" | "investment_return" | "other";
    direction?: "outflow" | "inflow" | null;
    counterparty?: string | null;
    note?: string | null;
    account_id?: string | null;
    investment_name?: string | null;
    investment_category_id?: string | null;
    investment_contact_name?: string | null;
    investment_contact_phone?: string | null;
    investment_contact_email?: string | null;
    tranches: { amount_cents: number; due_date: string }[];
  }) =>
    post<{ commitment_id: string; tranches: number; total_cents: number }>(
      "/api/obligations/schedule",
      body,
    ),

  /**
   * Record one dated event as several separately-typed commitments (Sarah,
   * 2026-10-14) — an investment repayment split into return of capital
   * (investment_return) and investment income (income). Each part becomes its
   * own standalone commitment on the shared date, in its own type column; they
   * are created together in one transaction.
   */
  createObligationParts: (body: {
    entity_id: string;
    due_date: string;
    counterparty?: string | null;
    note?: string | null;
    account_id?: string | null;
    parts: {
      kind: "investment" | "loan_given" | "loan_return" | "income" | "investment_return";
      amount_cents: number;
    }[];
  }) =>
    post<{ created: string[]; count: number }>("/api/obligations/parts", body),

  /**
   * Edit a commitment. Amount, date, who, note and account are always editable;
   * entity, kind and direction too for a plain standalone one (the server
   * refuses the structural change on a tranche or a loan with a return leg).
   */
  updateObligation: (
    id: string,
    body: {
      amount_cents: number;
      due_date: string;
      counterparty?: string | null;
      note?: string | null;
      account_id?: string | null;
      entity_id?: string;
      kind?:
        | "investment"
        | "loan_given"
        | "loan_return"
        | "income"
        | "investment_return"
        | "other";
      direction?: "outflow" | "inflow" | null;
      investment_name?: string | null;
      investment_category_id?: string | null;
      investment_contact_name?: string | null;
      investment_contact_phone?: string | null;
      investment_contact_email?: string | null;
    },
  ) => patch<{ id: string }>(`/api/obligations/${id}`, body),

  /** Settle (it happened), cancel (it will not), or reopen a commitment. */
  setObligationStatus: (
    id: string,
    status: "expected" | "settled" | "cancelled",
  ) =>
    post<{ id: string; status: string }>(`/api/obligations/${id}/status`, {
      status,
    }),

  /** Record part of a loan as paid (075). Paying the rest settles it. */
  addObligationPayment: (
    id: string,
    body: { amount_cents: number; paid_on: string; note: string | null },
  ) =>
    post<{ id: string; outstanding_cents: number; settled: boolean }>(
      `/api/obligations/${id}/payments`,
      body,
    ),

  /** Remove a payment recorded in error; a loan it had settled reopens. */
  deleteObligationPayment: (obligationId: string, paymentId: string) =>
    del<{ id: string; deleted: boolean; reopened: boolean }>(
      `/api/obligations/${obligationId}/payments/${paymentId}`,
    ),

  /** The documents on one obligation (065), newest first. */
  obligationDocuments: (obligationId: string) =>
    get<{ count: number; documents: ObligationDocument[] }>(
      `/api/obligations/${obligationId}/documents`,
    ),

  /**
   * Attach a document. The file is the raw request body with its own media
   * type as Content-Type — the same raw-body convention as extractInvoice, not
   * multipart — and the filename rides on the query string.
   */
  uploadObligationDocument: (obligationId: string, file: File) =>
    request<ObligationDocument>(
      `/api/obligations/${obligationId}/documents?filename=${encodeURIComponent(file.name)}`,
      {
        method: "POST",
        body: file,
        headers: { "Content-Type": file.type },
      },
    ),

  /** Remove a document from an obligation. */
  deleteObligationDocument: (obligationId: string, documentId: string) =>
    del<{ id: string; deleted: boolean }>(
      `/api/obligations/${obligationId}/documents/${documentId}`,
    ),

  /** The URL the browser downloads a document from. The session cookie rides
   *  with the navigation, and the server sets the attachment disposition. */
  obligationDocumentUrl: (obligationId: string, documentId: string) =>
    `/api/obligations/${obligationId}/documents/${documentId}`,

  /** Payroll registers (#17): list, upload (a file OR pasted text), retire. */
  payrollRegisters: () =>
    get<{ registers: PayrollRegisterRow[] }>("/api/payroll/registers"),
  uploadPayrollRegister: (
    entityId: string,
    opts: { file?: File; text?: string },
  ) => {
    // Multipart: the browser sets the Content-Type (with the boundary) when the
    // body is a FormData, so no header is passed here.
    const fd = new FormData();
    fd.append("entity_id", entityId);
    if (opts.file) fd.append("file", opts.file);
    if (opts.text) fd.append("text", opts.text);
    return request<PayrollUploadResult>("/api/payroll/registers", {
      method: "POST",
      body: fd,
    });
  },
  removePayrollRegister: (id: string) =>
    del<{ ok: boolean }>(`/api/payroll/registers/${id}`),

  possibleDuplicates: (q: {
    entity_id: string;
    vendor_id: string;
    amount_cents: number;
    invoice_date?: string | null;
  }) => {
    const query = new URLSearchParams({
      entity_id: q.entity_id,
      vendor_id: q.vendor_id,
      amount_cents: String(q.amount_cents),
    });
    if (q.invoice_date) query.set("invoice_date", q.invoice_date);
    return get<{ count: number; candidates: PossibleDuplicate[] }>(
      `/api/invoices/possible-duplicates?${query.toString()}`,
    );
  },

  approvals: (state = "pending") =>
    get<{
      state: string;
      count: number;
      approvals: Approval[];
      mine_to_decide: number;
      may_approve: boolean;
    }>(`/api/approvals?state=${encodeURIComponent(state)}`),

  decideApproval: (id: string, body: { action: "approve" | "decline"; note: string }) =>
    post<{ id: string; state: string }>(`/api/approvals/${id}/decide`, body),

  // AP inbox review queue (081). The document is served straight from the
  // endpoint (cookie-authed), so the screen links to apInboxDocumentUrl rather
  // than fetching bytes through the client.
  apInbox: (
    state: ApInboxItem["state"] = "needs_review",
    scope: "mailbox" | "trello" = "mailbox",
  ) =>
    get<{
      state: string;
      scope: string;
      items: ApInboxItem[];
      counts: ApInboxCounts;
      configured: boolean;
      armed: boolean;
    }>(`/api/ap-inbox?state=${encodeURIComponent(state)}&scope=${scope}`),

  commitApItem: (
    id: string,
    body: {
      entity_id: string;
      vendor_id: string;
      amount_cents: number;
      invoice_number?: string | null;
      invoice_date?: string | null;
      due_date?: string | null;
    },
  ) => post<{ id: string; status: string }>(`/api/ap-inbox/${id}/commit`, body),

  dismissApItem: (id: string, reason: string) =>
    post<{ id: string; state: string }>(`/api/ap-inbox/${id}/dismiss`, { reason }),

  // Pull a bucketed notice back onto the review queue (088) — the override when
  // the classifier set aside something that is in fact a bill. Moves it to
  // needs_review; it does not file anything.
  treatApItemAsInvoice: (id: string) =>
    post<{ id: string; state: string }>(`/api/ap-inbox/${id}/treat-as-invoice`, {}),

  apInboxIngestNow: () =>
    post<{
      dry_run?: boolean;
      listed?: number;
      ingested?: number;
      extracted?: number;
      failed?: number;
      error?: string;
      skipped?: string;
    }>("/api/ap-inbox/ingest-now"),

  apInboxDocumentUrl: (id: string) => `/api/ap-inbox/${id}/document`,

  payments: (state?: string) =>
    get<{ count: number; payments: Payment[]; prepared_cents: number }>(
      state ? `/api/payments?state=${encodeURIComponent(state)}` : "/api/payments",
    ),

  preparePayment: (body: {
    entity_id: string;
    vendor_id: string;
    invoice_ids: string[];
    method: string;
    scheduled_for?: string | null;
  }) => post<{ id: string; amount_cents: number }>("/api/payments", body),

  submitPayment: (id: string) =>
    post<{ id: string; state: string }>(`/api/payments/${id}/submit`),

  /** Records that a PERSON sent it. Nothing in this app sends a payment. */
  recordRelease: (id: string, body: { bank_reference: string; released_on?: string | null }) =>
    post<{ id: string; state: string }>(`/api/payments/${id}/release`, body),

  // --- Check printing (101) -------------------------------------------------
  /** Print a check for a released payment. Returns the PDF (open it to print)
   * and the assigned number. Produces paper to sign and mail — sends no money. */
  printCheck: async (id: string): Promise<{ blob: Blob; checkNumber: string | null }> => {
    const res = await fetch(`/api/payments/${id}/print-check`, {
      method: "POST",
      credentials: "same-origin",
    });
    if (!res.ok) {
      let detail = res.statusText;
      try {
        detail = ((await res.json()) as { detail?: string }).detail ?? detail;
      } catch {
        /* non-JSON error body */
      }
      throw new ApiError(res.status, detail);
    }
    return { blob: await res.blob(), checkNumber: res.headers.get("X-Check-Number") };
  },
  voidCheck: (id: string, reason: string) =>
    post<{ payment_id: string; voided_check_number: number }>(
      `/api/payments/${id}/void-check`, { reason },
    ),
  checkConfig: (accountId: string) =>
    get<{ config: CheckConfig | null }>(`/api/accounts/${accountId}/check-config`),
  saveCheckConfig: (accountId: string, body: {
    routing_number: string;
    account_number: string;
    drawer_name: string;
    drawer_address?: string | null;
    bank_name: string;
    next_check_number: number;
  }) => put<{ account_id: string; next_check_number: number; updated: boolean }>(
    `/api/accounts/${accountId}/check-config`, body,
  ),
  setCheckNumber: (accountId: string, next_check_number: number) =>
    post<{ account_id: string; old: number; next_check_number: number; warning: string | null }>(
      `/api/accounts/${accountId}/check-number`, { next_check_number },
    ),
  checkFontStatus: () =>
    get<{ installed: boolean; vendor: string }>("/api/check-font"),
  uploadCheckFont: (file: Blob) =>
    request<{ installed: boolean; bytes: number }>("/api/check-font", {
      method: "POST",
      body: file,
      headers: { "Content-Type": "font/ttf" },
    }),
};
